import { useState, useEffect, Component } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useUserProfile } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { CalculatedBadge } from "@/components/we360/CalculatedBadge";
import { AlertBanner } from "@/components/we360/AlertBanner";
import { CollapsibleCard } from "./CollapsibleCard";
import { MeasurementHistoryContent } from "./MeasurementHistory";
import { PARQConsentGate } from "@/components/we360/PARQConsentGate";
import {
  generateAlerts, ipaqLabel, dietCatLabel, behaviorCatLabel,
  hydrationLabel, sedLabel, emotionalLabel, anxietyLabel,
  stressLabel, wellbeingLabel, semanticColor,
} from "./helpers";
import { loadThresholds, persistAlert, loadOmsConfig } from "@/services/alertsService";
import { calculateIMC, classifyIMC, classifyBP, classifyGlucose } from "@/pages/FormF1/helpers";
import { PREDEFINED_CONDITIONS } from "@/data/loinc";
import {
  Heart, Activity, Footprints, Apple, Brain,
  CheckCircle, Clock, ArrowRight, ChevronDown, User, Sparkles,
  Plus, ClipboardList, Siren, Bell, X, AlertOctagon, RefreshCw,
  Camera, Lock, Unlock, Trophy, TrendingUp, Moon, Eye, EyeOff,
  Zap, Shield, Wind, Award, AlertTriangle, Smile, Flame, Info
} from "lucide-react";
import { listMeasurements } from "@/services/measurements";
import { HeroRadar } from "./charts/HeroRadar";
import { ImcGauge } from "./charts/ImcGauge";
import { DietDonut } from "./charts/DietDonut";
import { EmotionalThermo } from "./charts/EmotionalThermo";
import { normalizeRadarData } from "./charts/normalize";
import WellnessBalancesWidget from "./WellnessBalancesWidget";
import { WhatsAppDailyFeedWidget } from "./WhatsAppDailyFeedWidget";
import PersonalRadarCard from "./PersonalRadarCard";
import PersonalTriageCard from "./PersonalTriageCard";
import PersonalPopulationBenchmarkCard from "./PersonalPopulationBenchmarkCard";

// ── Error Boundary ────────────────────────────────────────
class DashboardErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, errorInfo) {
    console.error("[Dashboard] Uncaught error boundary exception:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6"
          style={{ background: "#FCFAEE" }}>
          <div className="w-14 h-14 rounded-full flex items-center justify-center"
            style={{ background: "#FEE2E2" }}>
            <AlertOctagon size={28} style={{ color: "#B91C1C" }} />
          </div>
          <p className="text-base font-semibold text-center" style={{ color: "#1E293B" }}>
            Ocurrió un error al cargar tu dashboard
          </p>
          <p className="text-sm text-center" style={{ color: "#71644C" }}>
            Esto puede ocurrir luego de un escaneo. Recarga la página para continuar.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-white text-sm"
            style={{ background: "#00628F" }}
          >
            <RefreshCw size={15} /> Recargar
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
const DIMS = [
  { key: "clinical", label: "Clínico", color: "#00628F", icon: Heart },
  { key: "physical", label: "Físico", color: "#00628F", icon: Activity },
  { key: "activity", label: "Actividad", color: "#F9A01B", icon: Footprints },
  { key: "nutrition", label: "Nutrición", color: "#157346", icon: Apple },
  { key: "emotional", label: "Emocional", color: "#883476", icon: Brain },
];

// ── Mapping de alertas críticas a sistema corporal afectado ───────────────────
// Permite al modal explicar QUÉ se detectó y qué sistema se ve afectado,
// en lugar de un texto genérico "cardiovascular" para cualquier tipo.
const CRITICAL_TYPE_INFO = {
  imc:             { label: "IMC",              system: "cardiometabólico" },
  pa:              { label: "Presión arterial", system: "cardiovascular" },
  oximetria:       { label: "Oximetría",        system: "respiratorio" },
  glucemia_hypo:   { label: "Glucemia baja",    system: "metabólico / neurológico" },
  glucemia_hyper:  { label: "Glucemia alta",    system: "metabólico / endocrino" },
};
function getCriticalInfo(alert) {
  if (alert.type === "glucemia") {
    const isHypo = (alert.text || "").toLowerCase().includes("hipo");
    return CRITICAL_TYPE_INFO[isHypo ? "glucemia_hypo" : "glucemia_hyper"];
  }
  return CRITICAL_TYPE_INFO[alert.type] || { label: "Valor crítico", system: "general" };
}
function formatAlertValue(alert) {
  if (alert.type === "pa")        return `${alert.value} mmHg`;
  if (alert.type === "oximetria") return `${alert.value}%`;
  if (alert.type === "glucemia")  return `${alert.value} mg/dl`;
  if (alert.type === "imc")       return `${alert.value} kg/m²`;
  return String(alert.value ?? "");
}

export default function Dashboard() {
  return (
    <DashboardErrorBoundary>
      <DashboardContent />
    </DashboardErrorBoundary>
  );
}

function DashboardContent() {
  const { profile, setProfile, clientId, personalEmail } = useUserProfile();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const hasF1 = !!profile.completedAt;
  const hasF2 = !!profile.f2Results?.completedAt;
  const hasF3 = !!profile.f3Results?.completedAt;
  const hasF4 = !!profile.f4Results?.completedAt;
  const hasF5 = !!profile.f5Results?.completedAt;
  const completedCount = [hasF1, hasF2, hasF3, hasF4, hasF5].filter(Boolean).length;
  const allComplete = completedCount === 5;
  const firstIncompleteForm = !hasF1 ? "/f1" : !hasF2 ? "/f2" : !hasF3 ? "/f3" : !hasF4 ? "/f4" : "/f5";

  const [thresholds, setThresholds] = useState(null);
  const [omsConfig, setOmsConfig] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [stickyDismissed, setStickyDismissed] = useState(false);
  const [showCriticalModal, setShowCriticalModal] = useState(false);
  const [showParqModal, setShowParqModal] = useState(false);

  // PAR-Q: si la evaluación física detectó riesgos y el usuario aún no resuelve
  // su autorización médica, debe ver un llamado a la acción claro en el Dashboard.
  const parqAlerta = profile.f2Results?.parqAlerta;
  const parqRiskCount = profile.f2Results?.parqRiskCount || 0;
  const needsParqConsent = hasF2 && parqAlerta && !profile.parqConsentimiento;

  const handleParqResolved = (type) => {
    setProfile((prev) => ({ ...prev, parqConsentimiento: type }));
    setShowParqModal(false);
  };

  // Cargar umbrales y alertas no vistas
  useEffect(() => {
    Promise.all([loadThresholds(), loadOmsConfig()]).then(([t, oms]) => {
      setThresholds(t);
      setOmsConfig(oms);
      const generated = generateAlerts(profile, t, oms);
      setAlerts(generated);
      // Persistir nuevas alertas críticas/danger
      if (user?.uid) {
        generated.filter((a) => ["critical", "danger"].includes(a.variant))
          .forEach((a) => persistAlert(user.uid, a).catch(() => {}));
      }
    });
  }, [profile, user?.uid]);

  // Obtener alertas no vistas para sticky banner - ya no necesario, usamos criticalAlerts directo

  const hasCritical = alerts.some((a) => a.variant === "critical");
  const hasDanger = alerts.some((a) => a.variant === "danger");
  // Critical siempre visible; danger abierto por defecto; warning/info colapsable
  const [warningOpen, setWarningOpen] = useState(false);
  const criticalAlerts = alerts.filter((a) => a.variant === "critical");
  const dangerAlerts = alerts.filter((a) => a.variant === "danger");
  const lowerAlerts = alerts.filter((a) => ["warning", "info"].includes(a.variant));

  const { isSupported: pushSupported, permission: pushPermission,
          subscribed: pushSubscribed, loading: pushLoading, subscribe: subscribePush } =
    usePushNotifications(user?.uid);
  const [pushBannerDismissed, setPushBannerDismissed] = useState(
    () => localStorage.getItem("we360_push_dismissed") === "1"
  );
  const showPushBanner = pushSupported && pushPermission === "default" && !pushSubscribed && !pushBannerDismissed;
  const [savedToast, setSavedToast] = useState(
    () => location.state?.measurementSaved
      ? (location.state.method === "camera" ? "camera" : "manual")
      : null
  );
  // Limpia el state de la ruta para que no reaparezca en back/refresh
  useEffect(() => {
    if (location.state?.measurementSaved) window.history.replaceState({}, document.title);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // F1 computed values
  const imc = hasF1 ? calculateIMC(profile.peso, profile.talla) : null;
  const imcCls = classifyIMC(imc);
  const bpCls = hasF1 ? classifyBP(profile.paSistolica, profile.paDiastolica) : null;
  const gluCls = hasF1 ? classifyGlucose(profile.glucemia) : null;

  // Hero dimension data
  function getHeroData() {
    const items = [];
    if (hasF1 && imcCls) items.push({ ...DIMS[0], value: imcCls.label, dotColor: semanticColor(imcCls.label) });
    if (hasF2) items.push({ ...DIMS[1], value: profile.f2Results.clasificacion6mwt || "—", dotColor: semanticColor(profile.f2Results.clasificacion6mwt) });
    if (hasF3) items.push({ ...DIMS[2], value: ipaqLabel(profile.f3Results.clasificacionIPAQ), dotColor: semanticColor(ipaqLabel(profile.f3Results.clasificacionIPAQ)) });
    if (hasF4) items.push({ ...DIMS[3], value: dietCatLabel(profile.f4Results.categoriaCalidadDieta), dotColor: semanticColor(dietCatLabel(profile.f4Results.categoriaCalidadDieta)) });
    if (hasF5) items.push({ ...DIMS[4], value: `${profile.f5Results.scoreGlobal}/100`, dotColor: semanticColor(emotionalLabel(profile.f5Results.categoriaGlobal)) });
    return items;
  }
  const heroItems = getHeroData();
  const radarData = normalizeRadarData(profile, { hasF1, hasF2, hasF3, hasF4, hasF5 });

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("we360_dashboard_view") || "premium";
  });
  const [latestMeasurement, setLatestMeasurement] = useState(null);
  const [measurementsHistory, setMeasurementsHistory] = useState([]);
  const { isDemo, isImpersonating, impersonatedUser } = useUserProfile();
  const targetUid = isImpersonating ? impersonatedUser?.uid : user?.uid;

  useEffect(() => {
    if (!targetUid || isDemo) {
      setLatestMeasurement(null);
      setMeasurementsHistory([]);
      return;
    }
    (async () => {
      try {
        const list = await listMeasurements(targetUid, 15);
        setMeasurementsHistory(list || []);
        if (list && list.length > 0) {
          setLatestMeasurement(list[0]);
        }
      } catch (e) {
        console.error("Error fetching latest measurements:", e);
      }
    })();
  }, [targetUid, isDemo]);

  // Auto-drenado de Outbox en Dashboard por si algún formulario F1..F5 quedó en PENDING_FHIR
  useEffect(() => {
    const email = user?.email || profile?.email;
    if (!email || isDemo || isImpersonating) return;
    const hasPendingForms = Boolean(
      profile?.pendingFhirF1 ||
      profile?.pendingFhirF2 ||
      profile?.pendingFhirF3 ||
      profile?.pendingFhirF4 ||
      profile?.pendingFhirF5
    );
    if (!hasPendingForms) return;

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { syncF1, syncF2, syncF3, syncF4, syncF5 } = await import("@/services/backendSync");
        const clearedFlags = {};
        let allOk = true;

        if (profile.pendingFhirF1) {
          const r = await syncF1(profile, email);
          if (r?.ok) clearedFlags.pendingFhirF1 = false;
          else allOk = false;
        }
        if (profile.pendingFhirF2 && profile.f2Results) {
          const r = await syncF2(profile.f2Results, email);
          if (r?.ok) clearedFlags.pendingFhirF2 = false;
          else allOk = false;
        }
        if (profile.pendingFhirF3 && profile.f3Results) {
          const r = await syncF3(profile.f3Results, email);
          if (r?.ok) clearedFlags.pendingFhirF3 = false;
          else allOk = false;
        }
        if (profile.pendingFhirF4 && profile.f4Results) {
          const r = await syncF4(profile.f4Results, email);
          if (r?.ok) clearedFlags.pendingFhirF4 = false;
          else allOk = false;
        }
        if (profile.pendingFhirF5 && profile.f5Results) {
          const r = await syncF5(profile.f5Results, email);
          if (r?.ok) clearedFlags.pendingFhirF5 = false;
          else allOk = false;
        }

        if (!cancelled && Object.keys(clearedFlags).length > 0) {
          setProfile((prev) => ({
            ...prev,
            ...clearedFlags,
            ...(allOk
              ? { fhirSyncStatus: "SYNCED", fhirSyncedAt: new Date().toISOString() }
              : {}),
          }));
        }
      } catch (err) {
        console.warn("[Dashboard Outbox] Retry failed:", err);
      }
    }, 2500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    user?.email,
    profile?.email,
    profile?.pendingFhirF1,
    profile?.pendingFhirF2,
    profile?.pendingFhirF3,
    profile?.pendingFhirF4,
    profile?.pendingFhirF5,
    isDemo,
    isImpersonating,
    setProfile,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  const [trackerTab, setTrackerTab] = useState("sleep");

  const toggleViewMode = () => {
    const nextMode = viewMode === "premium" ? "classic" : "premium";
    setViewMode(nextMode);
    localStorage.setItem("we360_dashboard_view", nextMode);
  };

  if (viewMode === "classic") {
    return (
      <div className="min-h-screen font-sans px-3 py-4 sm:px-4" style={{ background: "#FCFAEE" }}>
        <div className="w-full max-w-[420px] mx-auto">

          {/* ═══ STICKY CRITICAL ALERT — siempre primero, máxima visibilidad ═══ */}
          {criticalAlerts.length > 0 && !stickyDismissed && (
            <div
              className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4 we360-pulse-border"
              style={{ background: "#FFF0F0", border: "2px solid #C0453A" }}
              data-testid="dashboard-sticky-critical"
            >
              <Siren size={16} color="#9B1C1C" className="shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold" style={{ color: "#9B1C1C" }}>
                  {criticalAlerts.length} alerta{criticalAlerts.length > 1 ? "s" : ""} de atención inmediata
                </p>
                <p className="text-[11px]" style={{ color: "#C0453A" }}>
                  Consulta con un médico a la brevedad posible
                </p>
              </div>
              <button
                onClick={() => setShowCriticalModal(true)}
                className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                style={{ background: "rgba(192,69,58,0.12)", color: "#9B1C1C", border: "none", cursor: "pointer" }}
                data-testid="dashboard-sticky-close"
                aria-label="Cerrar alerta"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
          )}

          {/* ═══ PAR-Q — Autorización médica requerida (acción requerida) ═══ */}
          {needsParqConsent && (
            <div
              className="flex items-start gap-3 px-4 py-3.5 rounded-xl mb-4"
              style={{ background: "#FEF3C7", border: "2px solid #FCD34D" }}
              data-testid="dashboard-parq-banner"
            >
              <AlertOctagon size={18} color="#D97706" className="shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold" style={{ color: "#92400E" }}>
                  Acción requerida: autorización médica
                </p>
                <p className="text-[11px] leading-relaxed mb-2.5" style={{ color: "#78350F" }}>
                  Detectamos {parqRiskCount} {parqRiskCount === 1 ? "factor de riesgo" : "factores de riesgo"} en
                  tu evaluación física (PAR-Q). Sube tu autorización médica para poder
                  registrar mediciones de forma segura.
                </p>
                <button
                  onClick={() => setShowParqModal(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white transition-transform active:scale-[0.98]"
                  style={{ background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)", border: "none", cursor: "pointer" }}
                  data-testid="dashboard-parq-cta"
                >
                  <ClipboardList size={14} /> Resolver autorización
                </button>
              </div>
            </div>
          )}

          {/* ═══ MODAL — Subir autorización médica (PAR-Q) inline ═══ */}
          {showParqModal && (
            <div
              className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6 overflow-y-auto"
              data-testid="dashboard-parq-modal"
            >
              <div className="w-full max-w-md my-auto">
                <div className="flex justify-end mb-2">
                  <button
                    onClick={() => setShowParqModal(false)}
                    className="w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: "rgba(255,255,255,0.9)", border: "none", cursor: "pointer" }}
                    data-testid="dashboard-parq-modal-close"
                    aria-label="Cerrar"
                  >
                    <X size={16} color="#1E293B" />
                  </button>
                </div>
                <PARQConsentGate riskCount={parqRiskCount} onResolved={handleParqResolved} />
              </div>
            </div>
          )}

          {/* ═══ MODAL — Importancia de atención médica ═══ */}
          {showCriticalModal && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center px-4"
              style={{ background: "rgba(0,0,0,0.55)" }}
              onClick={() => setShowCriticalModal(false)}
              data-testid="critical-modal-overlay"
            >
              <div
                className="w-full max-w-[380px] rounded-2xl p-5"
                style={{ background: "white", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}
                onClick={(e) => e.stopPropagation()}
                data-testid="critical-modal"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                  style={{ background: "#FEE2E2" }}
                >
                  <AlertOctagon size={24} color="#9B1C1C" />
                </div>
                <h3 className="text-base font-bold mb-2" style={{ color: "#1F2937" }}>
                  Esto no es una alerta cualquiera
                </h3>
                <p className="text-sm leading-relaxed mb-3" style={{ color: "#4B5563" }}>
                  Detectamos {criticalAlerts.length === 1 ? "un valor" : `${criticalAlerts.length} valores`} en tus mediciones que {criticalAlerts.length === 1 ? "supera" : "superan"} los <strong>umbrales clínicos críticos</strong> definidos por la AHA/ACC, ADA y OMS.
                </p>

                {/* Sección dinámica — qué se detectó en tu caso */}
                <div className="rounded-xl p-3 mb-3" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
                  <p className="text-xs font-semibold mb-2" style={{ color: "#991B1B" }}>
                    Qué detectamos en tu caso:
                  </p>
                  <ul className="space-y-2">
                    {criticalAlerts.map((a, i) => {
                      const info = getCriticalInfo(a);
                      return (
                        <li key={i} className="flex items-start gap-2" data-testid={`critical-modal-item-${i}`}>
                          <span className="text-[14px] leading-none mt-0.5" style={{ color: "#C0453A" }}>•</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[13px] font-semibold" style={{ color: "#7C2D12" }}>
                              {info.label}: {formatAlertValue(a)}
                            </p>
                            <p className="text-[11px]" style={{ color: "#9A3412" }}>
                              Sistema {info.system}
                            </p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Razones genéricas — siempre las mismas (legalmente seguras) */}
                <div className="rounded-xl p-3 mb-3" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}>
                  <p className="text-xs font-semibold mb-1" style={{ color: "#9A3412" }}>
                    ¿Por qué es importante actuar ahora?
                  </p>
                  <ul className="text-[12px] space-y-1.5" style={{ color: "#7C2D12" }}>
                    <li>• Estos valores se asocian a <strong>complicaciones evitables</strong> si se tratan a tiempo.</li>
                    <li>• Una consulta médica oportuna permite <strong>confirmar el diagnóstico</strong> y prevenir un evento serio.</li>
                    <li>• WE360 detecta señales, pero el diagnóstico y tratamiento los hace tu médico.</li>
                  </ul>
                </div>
                <p className="text-[11px] mb-4 leading-relaxed" style={{ color: "#6B7280" }}>
                  Si cierras este aviso, la alerta seguirá visible en la sección &ldquo;Atención inmediata&rdquo; más abajo. Te recomendamos agendar una consulta médica en las próximas 24 horas.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCriticalModal(false)}
                    className="flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors hover:bg-slate-100"
                    style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #E2E8F0", cursor: "pointer" }}
                    data-testid="critical-modal-keep"
                  >
                    Mantener visible
                  </button>
                  <button
                    onClick={() => { setStickyDismissed(true); setShowCriticalModal(false); }}
                    className="flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold"
                    style={{ background: "#C0453A", color: "white", border: "none", cursor: "pointer" }}
                    data-testid="critical-modal-dismiss"
                  >
                    Entiendo, cerrar
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── Banner de riesgo: B2B sin email personal ── */}
          {clientId && !personalEmail && (
            <div
              className="flex items-start gap-3 px-4 py-3 rounded-xl mb-4 cursor-pointer"
              style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}
              onClick={() => navigate("/mi-cuenta")}
              data-testid="dashboard-risk-banner"
            >
              <span className="text-base shrink-0" style={{ lineHeight: 1 }}>⚠️</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold" style={{ color: "#92400E" }}>
                  Protege tu historial de salud
                </p>
                <p className="text-[11px]" style={{ color: "#B45309" }}>
                  Tu acceso depende de tu cuenta corporativa. Agrega tu email personal en Mi Cuenta →
                </p>
              </div>
            </div>
          )}

          {/* ═══ HEADER ═══ */}
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex-1 min-w-0">
              <div className="font-bold tracking-[1.5px] uppercase" style={{ fontSize: 10, color: "#71644C" }}>WE 360</div>
              <div className="text-lg font-bold mt-0.5 leading-snug" style={{ color: "#2D2A24" }} data-testid="dashboard-title">
                {hasF1 ? `Hola ${profile.nombres}, aquí está tu panorama de salud` : "Tu panorama de salud"}
              </div>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-sm font-bold"
                style={{ background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)" }}
              >
                {hasF1 ? profile.nombres?.[0]?.toUpperCase() : "W"}
              </div>
            </div>
          </div>

          {/* Dynamic View Toggle Bar - Classic to Premium */}
          <div className="mb-5 flex justify-end">
            <button
              onClick={toggleViewMode}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#00628F]/30 bg-[#00628F]/5 text-xs font-bold text-[#00628F] hover:bg-[#00628F]/10 transition-colors"
              data-testid="toggle-view-premium"
            >
              <Sparkles size={12} className="text-[#2A9D8F]" />
              Activar Vista Premium
            </button>
          </div>

          {/* Date + completion badge */}
          <div className="flex items-center justify-between mb-5">
            <span className="text-[11px]" style={{ color: "#71644C" }}>
              Generado el {new Date().toLocaleDateString("es-CO")}
            </span>
            <span
              className="text-[10px] font-bold px-2.5 py-1 rounded-lg"
              style={{
                background: completedCount === 5 ? "#E2F0E8" : "#FFF3DC",
                color: completedCount === 5 ? "#157346" : "#F9A01B",
              }}
              data-testid="dashboard-completion-badge"
            >
              {completedCount}/5 evaluaciones completadas
            </span>
          </div>

          {/* ═══ SECTION 1: HERO ═══ */}
          {heroItems.length > 0 && (
            <div className="we-card p-4 mb-4" data-testid="dashboard-hero">
              {/* Radar chart / bars */}
              {radarData.length > 0 && (
                <div className="mb-3">
                  <HeroRadar data={radarData} />
                </div>
              )}
              {/* Dimension summary dots */}
              <div className="flex justify-around gap-1 pt-2" style={{ borderTop: radarData.length > 0 ? "1px solid #E2D9CB" : "none" }}>
                {heroItems.map((item) => (
                  <div key={item.key} className="flex flex-col items-center text-center flex-1 min-w-0">
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center mb-1.5"
                      style={{ background: `${item.color}15` }}
                    >
                      <item.icon size={16} color={item.color} />
                    </div>
                    <span className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: "#71644C" }}>
                      {item.label}
                    </span>
                    <span className="text-[11px] font-bold mt-0.5" style={{ color: "#2D2A24" }}>
                      {item.value}
                    </span>
                    <div
                      className="w-2 h-2 rounded-full mt-1"
                      style={{ background: item.dotColor }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ RECOMMENDATIONS CTA ═══ */}
          {allComplete ? (
            <button
              onClick={() => navigate("/recommendations")}
              className="w-full cursor-pointer active:scale-[0.97] transition-transform mb-4"
              style={{
                padding: 14,
                borderRadius: 14,
                border: "none",
                background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)",
                color: "white",
                fontSize: 13,
                fontWeight: 700,
                boxShadow: "0 6px 20px rgba(0,98,143,0.20)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
              data-testid="dashboard-recommendations-cta"
            >
              <Sparkles size={16} /> Ver mis recomendaciones personalizadas
            </button>
          ) : completedCount > 0 ? (
            <div className="text-center mb-4 p-3 rounded-xl" style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }} data-testid="dashboard-recommendations-locked">
              <p className="text-[11px]" style={{ color: "#B5A998" }}>
                Completa tus {5 - completedCount} evaluaciones restantes para desbloquear recomendaciones personalizadas
              </p>
            </div>
          ) : null}

          {/* ═══ TRIAJE CLÍNICO HI-OS PERSONAL ═══ */}
          <PersonalTriageCard
            profile={profile}
            latestMeasurement={latestMeasurement}
            isDemo={isDemo}
            onNavigateToMeasurement={() => navigate(firstIncompleteForm || "/measure")}
            className="mb-4"
          />

          {/* ═══ RADAR HOLÍSTICO 360° PERSONAL ═══ */}
          <PersonalRadarCard
            profile={profile}
            latestMeasurement={latestMeasurement}
            isDemo={isDemo}
            onNavigateToMeasurement={() => navigate("/measure")}
            className="mb-4"
          />

          {/* ═══ BENCHMARKING POBLACIONAL & PERCENTIL PERSONAL ═══ */}
          <PersonalPopulationBenchmarkCard
            profile={profile}
            latestMeasurement={latestMeasurement}
            isDemo={isDemo}
            onNavigateToMeasurement={() => navigate(firstIncompleteForm || "/measure")}
            className="mb-4"
          />

          {/* ═══ BALANCES DIARIOS DE BIENESTAR ═══ */}
          <WellnessBalancesWidget
            profile={profile}
            isDemo={isDemo}
            latestMeasurement={latestMeasurement}
            onNavigateToDiet={() => navigate("/f4")}
            onNavigateToActivity={() => navigate("/f3")}
            className="mb-4"
          />

          {/* ═══ WHATSAPP BIENESTAR EN TIEMPO REAL ═══ */}
          <WhatsAppDailyFeedWidget
            userId={targetUid}
            profile={profile}
            isDemo={isDemo}
            userPhone={profile?.whatsapp || profile?.telefono || profile?.phone}
            className="mb-4"
          />

          {/* ── Banner opt-in Push Notifications ── */}
          {showPushBanner && (
            <div
              className="flex items-center gap-3 px-4 py-3 rounded-xl mb-4"
              style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}
              data-testid="dashboard-push-banner"
            >
              <Bell size={16} color="#1D4ED8" className="shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold" style={{ color: "#1E40AF" }}>Activar alertas en tiempo real</p>
                <p className="text-[11px]" style={{ color: "#3B82F6" }}>
                  Recibe notificaciones cuando tus mediciones sean críticas, aunque la app esté cerrada.
                </p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  onClick={() => { subscribePush(); }}
                  disabled={pushLoading}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                  style={{ background: "#1D4ED8", color: "white", border: "none", cursor: "pointer" }}
                  data-testid="push-subscribe-btn"
                >
                  {pushLoading ? "..." : "Activar"}
                </button>
                <button
                  onClick={() => { localStorage.setItem("we360_push_dismissed","1"); setPushBannerDismissed(true); }}
                  className="text-[11px] px-2 py-1 rounded-lg"
                  style={{ background: "transparent", color: "#93C5FD", border: "1px solid #BFDBFE", cursor: "pointer" }}
                  data-testid="push-dismiss-btn"
                >
                  Ahora no
                </button>
              </div>
            </div>
          )}

          {/* ═══ SECTION 2: ALERTS ═══ */}
          {alerts.length > 0 && (
            <div className="mb-4" data-testid="dashboard-alerts">

              {/* Critical — siempre visibles, sin colapso */}
              {criticalAlerts.length > 0 && (
                <div className="space-y-2 mb-2" data-testid="dashboard-alerts-critical">
                  <p className="text-[11px] font-bold uppercase tracking-wide px-1 mb-1" style={{ color: "#9B1C1C" }}>
                    Atención inmediata ({criticalAlerts.length})
                  </p>
                  {criticalAlerts.map((a, i) => (
                    <AlertBanner key={i} variant="critical" testId={`alert-critical-${i}`}>
                      {a.text}
                    </AlertBanner>
                  ))}
                </div>
              )}

              {/* Danger — siempre visibles */}
              {dangerAlerts.length > 0 && (
                <div className="space-y-2 mb-2" data-testid="dashboard-alerts-danger">
                  <p className="text-[11px] font-bold uppercase tracking-wide px-1 mb-1" style={{ color: "#C0453A" }}>
                    Requieren atención ({dangerAlerts.length})
                  </p>
                  {dangerAlerts.map((a, i) => (
                    <AlertBanner key={i} variant="danger" testId={`alert-danger-${i}`}>
                      {a.text}
                    </AlertBanner>
                  ))}
                </div>
              )}

              {/* Warning / Info — colapsables */}
              {lowerAlerts.length > 0 && (
                <div data-testid="dashboard-alerts-lower">
                  <div
                    className="flex items-center justify-between cursor-pointer px-1 py-2"
                    onClick={() => setWarningOpen((o) => !o)}
                    data-testid="dashboard-alerts-toggle"
                  >
                    <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: "#71644C" }}>
                      Otras alertas ({lowerAlerts.length})
                    </p>
                    <ChevronDown
                      size={14}
                      color="#B5A998"
                      style={{ transform: warningOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.3s ease" }}
                    />
                  </div>
                  <div style={{ display: "grid", gridTemplateRows: warningOpen ? "1fr" : "0fr", transition: "grid-template-rows 0.35s ease" }}>
                    <div style={{ overflow: "hidden" }}>
                      <div className="space-y-2 pt-1">
                        {lowerAlerts.map((a, i) => (
                          <AlertBanner key={i} variant={a.variant} testId={`alert-lower-${i}`}>
                            {a.text}
                          </AlertBanner>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ═══ NUEVA MEDICIÓN + HISTORIAL (solo tras completar F1) ═══ */}
          {hasF1 && (
            <>
              {savedToast && (
                <div
                  className="mb-3 rounded-xl px-3 py-2.5 flex items-center gap-2"
                  style={{
                    background: "#E2F0E8",
                    border: "1px solid rgba(21,115,70,0.2)",
                  }}
                  data-testid="dashboard-measurement-saved-toast"
                >
                  <CheckCircle size={14} color="#157346" />
                  <span className="text-xs font-semibold flex-1" style={{ color: "#157346" }}>
                    {savedToast === "camera"
                      ? "Escaneo facial guardado — revísalo en Historial de mediciones"
                      : "Medición guardada correctamente"}
                  </span>
                  <button onClick={() => setSavedToast(null)} className="p-0.5 hover:opacity-60 transition-opacity cursor-pointer">
                    <X size={12} color="#157346" />
                  </button>
                </div>
              )}

              <button
                onClick={() => navigate("/medicion/nueva")}
                className="w-full cursor-pointer active:scale-[0.97] transition-transform mb-3"
                style={{
                  padding: 12,
                  borderRadius: 14,
                  border: "1px dashed #00628F",
                  background: "rgba(0,98,143,0.05)",
                  color: "#00628F",
                  fontSize: 13,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                data-testid="dashboard-new-measurement-cta"
              >
                <Plus size={16} /> Nueva medición de seguimiento
              </button>

              <div className="mb-4">
                <CollapsibleCard
                  title="Historial de mediciones"
                  subtitle="Últimos registros de seguimiento"
                  color="#0F766E"
                  icon={ClipboardList}
                  testId="card-measurement-history"
                >
                  {(isOpen) => <MeasurementHistoryContent isOpen={isOpen} />}
                </CollapsibleCard>
              </div>
            </>
          )}

          {/* ═══ SECTION 3: DETAIL CARDS ═══ */}
          <div className="space-y-3 mb-4">

            {/* Card 1: Clinical */}
            {hasF1 && (
              <CollapsibleCard
                title="Salud Clínica"
                subtitle={`IMC ${imc || "—"} (${imcCls?.label || "—"}) · PA ${bpCls?.label || "—"}`}
                color="#00628F"
                icon={Heart}
                badge={imcCls?.label}
                badgeColor={imcCls?.color}
                testId="card-clinical"
              >
                {(isOpen) => (
                <div className="space-y-2 mt-2">
                  {imc && (
                    <div className="mb-3">
                      <ImcGauge imc={imc} classification={imcCls} animate={isOpen} />
                    </div>
                  )}
                  {imc && (
                    <CalculatedBadge label="IMC" value={imc} category={imcCls?.label} categoryColor={imcCls?.color} testId="db-badge-imc" />
                  )}
                  {bpCls && (
                    <CalculatedBadge label="Presión arterial" value={`${profile.paSistolica}/${profile.paDiastolica}`} unit="mmHg" category={bpCls?.label} categoryColor={bpCls?.color} testId="db-badge-bp" />
                  )}
                  {gluCls && (
                    <CalculatedBadge label="Glucemia" value={profile.glucemia} unit="mg/dl" category={gluCls?.label} categoryColor={gluCls?.color} testId="db-badge-glucose" />
                  )}
                  {profile.oximetria && (
                    <CalculatedBadge
                      label="Oximetría"
                      value={profile.oximetria}
                      unit="%"
                      category={parseFloat(profile.oximetria) < 95 ? "Baja" : "Normal"}
                      categoryColor={parseFloat(profile.oximetria) < 95 ? "#C0453A" : "#157346"}
                      testId="db-badge-oxi"
                    />
                  )}
                  {profile.cintura && (
                    <CalculatedBadge label="Cintura abdominal" value={profile.cintura} unit="cm" testId="db-badge-cintura" />
                  )}

                  {/* Lifestyle info */}
                  <div className="rounded-xl p-3 mt-1" style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}>
                    <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: "#71644C" }}>Estilo de vida</p>
                    <div className="flex flex-wrap gap-1.5">
                      {profile.tabaco && <InfoChip label={`Tabaco: ${profile.tabaco}`} />}
                      {profile.alcohol && <InfoChip label={`Alcohol: ${profile.alcohol}`} />}
                      {profile.sueno && <InfoChip label={`Sueno: ${profile.sueno}h`} color={parseFloat(profile.sueno) < 7 ? "#F9A01B" : "#157346"} />}
                    </div>
                  </div>

                  {/* Conditions */}
                  {(() => {
                    const allConds = [
                      ...(profile.condicionIds || []).map((id) => PREDEFINED_CONDITIONS.find((p) => p.id === id)?.nombre).filter(Boolean),
                      ...(profile.condicionesAdicionales || []).map((c) => c.nombre),
                    ];
                    return allConds.length > 0 && (
                      <div className="rounded-xl p-3" style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}>
                        <p className="text-[10px] font-bold uppercase tracking-wide mb-2" style={{ color: "#71644C" }}>Condiciones detectadas</p>
                        <div className="flex flex-wrap gap-1.5">
                          {allConds.map((c) => <InfoChip key={c} label={c} color="#C0453A" />)}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Medications */}
                  {profile.usaMedicamentos === "Si" && profile.medicamentos && (
                    <div className="rounded-xl p-3" style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}>
                      <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: "#71644C" }}>Medicamentos</p>
                      <div className="flex flex-wrap gap-1.5">
                        {Array.isArray(profile.medicamentos) ? (
                          profile.medicamentos.map((med, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 rounded-full" style={{ background: "#E8F4F8", color: "#2D2A24" }}>
                              {med.nombre}{med.dosis ? ` (${med.dosis})` : ""}
                            </span>
                          ))
                        ) : (
                          <p className="text-xs" style={{ color: "#2D2A24" }}>{profile.medicamentos}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                )}
              </CollapsibleCard>
            )}

            {/* Card 2: Physical */}
            {hasF2 && (
              <CollapsibleCard
                title="Estado Físico"
                subtitle={`PAR-Q ${profile.f2Results.parqAlerta ? `${profile.f2Results.parqRiskCount} ${profile.f2Results.parqRiskCount === 1 ? "riesgo" : "riesgos"}` : "Apto"} · 6MWT ${profile.f2Results.clasificacion6mwt}`}
                color="#00628F"
                icon={Activity}
                badge={profile.f2Results.parqAlerta ? `${profile.f2Results.parqRiskCount} ${profile.f2Results.parqRiskCount === 1 ? "riesgo" : "riesgos"}` : "Apto"}
                badgeColor={profile.f2Results.parqAlerta ? "#C0453A" : "#157346"}
                testId="card-physical"
              >
                <div className="space-y-2 mt-2">
                  <CalculatedBadge
                    label="PAR-Q"
                    value={profile.f2Results.parqAlerta ? `${profile.f2Results.parqRiskCount} ${profile.f2Results.parqRiskCount === 1 ? "riesgo detectado" : "riesgos detectados"}` : "Apto"}
                    categoryColor={profile.f2Results.parqAlerta ? "#C0453A" : "#157346"}
                    testId="db-badge-parq"
                  />
                  <CalculatedBadge label="Caminata 6 min" value={`${profile.f2Results.caminata6min}m`} category={profile.f2Results.clasificacion6mwt} categoryColor={semanticColor(profile.f2Results.clasificacion6mwt)} testId="db-badge-walk" />
                  <CalculatedBadge label="Sentadillas" value={`${profile.f2Results.sentadillas} reps`} category={profile.f2Results.clasificacionSentadillas} categoryColor={semanticColor(profile.f2Results.clasificacionSentadillas)} testId="db-badge-squats" />
                  <CalculatedBadge label="Flexiones" value={`${profile.f2Results.flexiones} reps`} category={profile.f2Results.clasificacionFlexiones} categoryColor={semanticColor(profile.f2Results.clasificacionFlexiones)} testId="db-badge-pushups" />
                  <CalculatedBadge label="Flexibilidad" value={profile.f2Results.clasificacionFlexibilidad} categoryColor={semanticColor(profile.f2Results.clasificacionFlexibilidad)} testId="db-badge-flex" />
                </div>
              </CollapsibleCard>
            )}

            {/* Card 3: Activity */}
            {hasF3 && (
              <CollapsibleCard
                title="Actividad Física"
                subtitle={`${profile.f3Results.metTotal} MET-min/sem (${ipaqLabel(profile.f3Results.clasificacionIPAQ)})`}
                color="#F9A01B"
                icon={Footprints}
                badge={ipaqLabel(profile.f3Results.clasificacionIPAQ)}
                badgeColor={semanticColor(ipaqLabel(profile.f3Results.clasificacionIPAQ))}
                testId="card-activity"
              >
                <div className="space-y-2 mt-2">
                  <CalculatedBadge label="MET Total" value={profile.f3Results.metTotal} unit="MET-min/sem" category={ipaqLabel(profile.f3Results.clasificacionIPAQ)} categoryColor={semanticColor(ipaqLabel(profile.f3Results.clasificacionIPAQ))} testId="db-badge-met-total" />
                  <CalculatedBadge label="MET Vigorosa" value={profile.f3Results.vigorosa?.metMin ?? 0} unit="MET-min/sem" testId="db-badge-met-vig" />
                  <CalculatedBadge label="MET Moderada" value={profile.f3Results.moderada?.metMin ?? 0} unit="MET-min/sem" testId="db-badge-met-mod" />
                  <CalculatedBadge label="MET Caminata" value={profile.f3Results.caminata?.metMin ?? 0} unit="MET-min/sem" testId="db-badge-met-walk" />
                  <CalculatedBadge label="Sedentarismo" value={sedLabel(profile.f3Results.indiceSedentarismo)} categoryColor={semanticColor(sedLabel(profile.f3Results.indiceSedentarismo))} testId="db-badge-sed" />

                  {(profile.f3Results.indiceSedentarismo === "high" || profile.f3Results.indiceSedentarismo === "very_high") && (
                    <AlertBanner variant="warning" testId="db-alert-sed">
                      Pasar muchas horas sentado/a es un factor de riesgo independiente. Intenta hacer pausas activas cada hora.
                    </AlertBanner>
                  )}
                  {profile.f3Results.clasificacionIPAQ === "low" && (
                    <AlertBanner variant="info" testId="db-alert-oms">
                      La OMS recomienda al menos 150 min de actividad moderada o 75 min vigorosa por semana.
                    </AlertBanner>
                  )}
                </div>
              </CollapsibleCard>
            )}

            {/* Card 4: Nutrition */}
            {hasF4 && (
              <CollapsibleCard
                title="Nutrición"
                subtitle={`Dieta ${profile.f4Results.scoreCalidadDieta}/40 (${dietCatLabel(profile.f4Results.categoriaCalidadDieta)}) · Conductas ${profile.f4Results.scoreConductas}/15`}
                color="#157346"
                icon={Apple}
                badge={dietCatLabel(profile.f4Results.categoriaCalidadDieta)}
                badgeColor={semanticColor(dietCatLabel(profile.f4Results.categoriaCalidadDieta))}
                testId="card-nutrition"
              >
                {(isOpen) => (
                <div className="space-y-2 mt-2">
                  <div className="mb-3">
                    <DietDonut score={profile.f4Results.scoreCalidadDieta} category={profile.f4Results.categoriaCalidadDieta} animate={isOpen} />
                  </div>
                  <CalculatedBadge label="Calidad de dieta" value={`${profile.f4Results.scoreCalidadDieta}/40`} category={dietCatLabel(profile.f4Results.categoriaCalidadDieta)} categoryColor={semanticColor(dietCatLabel(profile.f4Results.categoriaCalidadDieta))} testId="db-badge-diet" />
                  <CalculatedBadge label="Conductas alimentarias" value={`${profile.f4Results.scoreConductas}/15`} category={behaviorCatLabel(profile.f4Results.categoriaConductas)} categoryColor={semanticColor(behaviorCatLabel(profile.f4Results.categoriaConductas))} testId="db-badge-behavior" />
                  <CalculatedBadge label="Hidratación" value={hydrationLabel(profile.f4Results.hidratacion)} categoryColor={semanticColor(hydrationLabel(profile.f4Results.hidratacion))} testId="db-badge-hydration" />

                  <div className="rounded-xl p-3 mt-1" style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}>
                    <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: "#71644C" }}>Motivacion: {profile.f4Results.nivelMotivacion}</p>
                    {profile.f4Results.motivaciones?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {profile.f4Results.motivaciones.map((m) => <InfoChip key={m} label={m} color="#157346" />)}
                      </div>
                    )}
                  </div>

                  <div className="rounded-xl p-3" style={{ background: "#FCFAEE", border: "1px solid #E2D9CB" }}>
                    <p className="text-[10px] font-bold uppercase tracking-wide mb-1" style={{ color: "#71644C" }}>Restricciones</p>
                    <p className="text-xs" style={{ color: "#2D2A24" }}>
                      {profile.f4Results.restricciones?.length > 0
                        ? profile.f4Results.restricciones.join(", ")
                        : "Ninguna"}
                    </p>
                  </div>
                </div>
                )}
              </CollapsibleCard>
            )}

            {/* Card 5: Emotional */}
            {hasF5 && (
              <CollapsibleCard
                title="Bienestar Emocional"
                subtitle={`Score global ${profile.f5Results.scoreGlobal}/100 (${emotionalLabel(profile.f5Results.categoriaGlobal)})`}
                color="#883476"
                icon={Brain}
                badge={`${profile.f5Results.scoreGlobal}/100`}
                badgeColor={semanticColor(emotionalLabel(profile.f5Results.categoriaGlobal))}
                testId="card-emotional"
              >
                {(isOpen) => (
                <div className="space-y-2 mt-2">
                  {/* Thermometer */}
                  <div className="mb-3">
                    <EmotionalThermo score={profile.f5Results.scoreGlobal} animate={isOpen} />
                  </div>

                  {/* Hero global score */}
                  <div
                    className="rounded-2xl p-4 text-center"
                    style={{
                      background: "linear-gradient(135deg, #883476 0%, #A04890 50%, #C070A0 100%)",
                      boxShadow: "0 6px 20px rgba(136,52,118,0.20)",
                    }}
                    data-testid="db-emotional-hero"
                  >
                    <div className="text-3xl font-extrabold text-white">{profile.f5Results.scoreGlobal}</div>
                    <p className="text-xs text-white mb-1" style={{ opacity: 0.7 }}>de 100</p>
                    <span className="inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-lg" style={{ background: "rgba(255,255,255,0.2)", color: "white" }}>
                      {emotionalLabel(profile.f5Results.categoriaGlobal)}
                    </span>
                    <p className="text-[11px] mt-2 leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
                      {getMotivationalPhrase(profile.f5Results.categoriaGlobal)}
                    </p>
                  </div>

                  <CalculatedBadge label="Ansiedad (GAD-5)" value={`${profile.f5Results.ansiedad?.score}/15`} category={anxietyLabel(profile.f5Results.ansiedad?.categoria)} categoryColor={semanticColor(anxietyLabel(profile.f5Results.ansiedad?.categoria))} testId="db-badge-anxiety" />
                  <CalculatedBadge label="Estrés / Burnout" value={`${profile.f5Results.estres?.score}/20`} category={stressLabel(profile.f5Results.estres?.categoria)} categoryColor={semanticColor(stressLabel(profile.f5Results.estres?.categoria))} testId="db-badge-stress" />
                  <CalculatedBadge label="Bienestar interno" value={`${profile.f5Results.bienestar?.score}/20`} category={wellbeingLabel(profile.f5Results.bienestar?.categoria)} categoryColor={semanticColor(wellbeingLabel(profile.f5Results.bienestar?.categoria))} testId="db-badge-wellbeing" />
                </div>
                )}
              </CollapsibleCard>
            )}
          </div>

          {/* ═══ SECTION 4: FORM STATUS ═══ */}
          <div className="we-card p-4 mb-4" data-testid="dashboard-form-status">
            <p className="text-[11px] font-bold uppercase tracking-wide mb-3" style={{ color: "#71644C" }}>
              Estado de evaluaciones
            </p>
            <div className="space-y-2">
              {[
                { n: 1, title: "Demográfico y Clínico", path: "/f1", done: hasF1, date: profile.completedAt },
                { n: 2, title: "Estado Físico", path: "/f2", done: hasF2, date: profile.f2Results?.completedAt },
                { n: 3, title: "Actividad Física", path: "/f3", done: hasF3, date: profile.f3Results?.completedAt },
                { n: 4, title: "Nutrición", path: "/f4", done: hasF4, date: profile.f4Results?.completedAt },
                { n: 5, title: "Bienestar Emocional", path: "/f5", done: hasF5, date: profile.f5Results?.completedAt },
              ].map((f) => (
                <div
                  key={f.n}
                  className="flex items-center justify-between p-2.5 rounded-xl cursor-pointer"
                  style={{ background: f.done ? "#E2F0E8" : "#FCFAEE", border: `1px solid ${f.done ? "rgba(21,115,70,0.15)" : "#E2D9CB"}` }}
                  onClick={() => navigate(f.path)}
                  data-testid={`form-status-f${f.n}`}
                >
                  <div className="flex items-center gap-2">
                    {f.done ? <CheckCircle size={14} color="#157346" /> : <Clock size={14} color="#B5A998" />}
                    <span className="text-xs font-semibold" style={{ color: f.done ? "#157346" : "#71644C" }}>
                      F{f.n}: {f.title}
                    </span>
                  </div>
                  {f.done ? (
                    <span className="text-[10px]" style={{ color: "#157346" }}>
                      {f.date && !isNaN(new Date(f.date).getTime()) ? new Date(f.date).toLocaleDateString("es-CO") : "—"}
                    </span>
                  ) : (
                    <span className="text-[10px] font-semibold flex items-center gap-1" style={{ color: "#00628F" }}>
                      Ir a completar <ArrowRight size={10} />
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* No forms message */}
          {completedCount === 0 && (
            <div className="we-card p-6 text-center mb-4" data-testid="dashboard-empty-state">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: "#E3F0F5" }}>
                <User size={24} color="#00628F" />
              </div>
              <h2 className="text-base font-bold mb-1" style={{ color: "#2D2A24" }}>Comienza tu evaluación</h2>
              <p className="text-xs mb-4" style={{ color: "#71644C" }}>
                Completa los 5 formularios para obtener tu panorama completo de salud.
              </p>
              <button
                onClick={() => navigate("/f1")}
                data-testid="dashboard-start-btn"
                className="w-full cursor-pointer active:scale-[0.97] transition-transform"
                style={{
                  padding: 14,
                  borderRadius: 14,
                  border: "none",
                  background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)",
                  color: "white",
                  fontSize: 14,
                  fontWeight: 700,
                  boxShadow: "0 6px 20px rgba(0,98,143,0.20)",
                }}
              >
                Comenzar Formulario 1
              </button>
            </div>
          )}

          {/* ═══ SECTION 5: FOOTER ═══ */}
          <div className="text-center py-5" data-testid="dashboard-footer">
            <p className="text-xs font-semibold" style={{ color: "#71644C" }}>
              WE 360 · Tu salud, tu camino
            </p>
            <p className="text-[10px] mt-1.5 leading-relaxed" style={{ color: "#B5A998" }}>
              Este informe es orientativo y no reemplaza una consulta médica profesional.
            </p>
            <p className="text-[10px] mt-1" style={{ color: "#B5A998" }}>
              Generado el {new Date().toLocaleDateString("es-CO")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── PREMIUM VIEW MODE ──────────────────────────────────────────────────
  const longevityScore = allComplete ? 85 : Math.round((completedCount / 5) * 85);

  // ── Helpers y Configuración de Biomarcadores de Face Scan ──
  const getUserAge = () => {
    if (!profile?.fechaNacimiento) return 35;
    try {
      const birth = new Date(profile.fechaNacimiento);
      const today = new Date();
      let age = today.getFullYear() - birth.getFullYear();
      const m = today.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return isNaN(age) ? 35 : age;
    } catch (e) {
      return 35;
    }
  };
  const userAge = getUserAge();

  const BIOMARKERS_CONFIG = [
    {
      key: "heartRate",
      label: "Frecuencia cardíaca",
      unit: "lpm",
      icon: Activity,
      explanation: "Frecuencia con la que late el corazón por minuto (lpm). El rango normal en reposo en adultos es de 60 a 100 lpm.",
      demoVal: 72,
    },
    {
      key: "heartRateVariability",
      label: "Variabilidad (HRV)",
      unit: "ms",
      icon: Zap,
      explanation: "Variabilidad entre latidos cardíacos sucesivos. Una mayor variabilidad indica un sistema nervioso autónomo más adaptable y saludable (valores > 50 ms son óptimos).",
      demoVal: 48,
    },
    {
      key: "paSistolica",
      label: "PA Sistólica",
      unit: "mmHg",
      icon: Shield,
      explanation: "Presión máxima en las arterias durante la sístole cardíaca (contracción). Nivel óptimo: menor a 120 mmHg.",
      demoVal: 118,
    },
    {
      key: "paDiastolica",
      label: "PA Diastólica",
      unit: "mmHg",
      icon: Shield,
      explanation: "Presión mínima en las arterias durante la diástole cardíaca (relajación). Nivel óptimo: menor a 80 mmHg.",
      demoVal: 78,
    },
    {
      key: "oximetria",
      label: "Saturación O₂",
      unit: "%",
      icon: Wind,
      explanation: "Porcentaje de oxígeno transportado por la hemoglobina en los glóbulos rojos. Nivel normal: 95% o superior.",
      demoVal: 98,
    },
    {
      key: "respirationRate",
      label: "Frecuencia resp.",
      unit: "rpm",
      icon: Wind,
      explanation: "Número de respiraciones completadas por minuto (rpm). El ritmo normal en reposo para adultos sanos es de 12 a 20 rpm.",
      demoVal: 14,
    },
    {
      key: "stressIndex",
      label: "Índice de estrés",
      unit: "",
      icon: Brain,
      explanation: "Índice de Baevsky que evalúa la carga reguladora del sistema nervioso autónomo. <150 es óptimo/relajado, 150-300 moderado, 300-500 alto, y >500 crítico.",
      demoVal: 145,
    },
    {
      key: "cardiovascularAge",
      label: "Edad cardiovascular",
      unit: "años",
      icon: Heart,
      explanation: "Edad estimada de tus vasos sanguíneos y sistema arterial según su elasticidad y velocidad de onda de pulso.",
      demoVal: userAge - 2 > 20 ? userAge - 2 : 25,
    },
    {
      key: "cardiovascularBMI",
      label: "IMC cardiovascular",
      unit: "",
      icon: Award,
      explanation: "Índice de Masa Corporal estimado a través de tu firma hemodinámica óptica.",
      demoVal: 23.2,
    },

    {
      key: "wellnessScore",
      label: "Puntaje Bienestar",
      unit: "/10",
      icon: Smile,
      explanation: "Puntuación sintética de balance de bienestar y autorregulación homeostática del cuerpo (escala de 1 a 10).",
      demoVal: 8.6,
    },
    {
      key: "ansIndexPNS",
      label: "Tono Parasimpático",
      unit: "",
      icon: Moon,
      explanation: "Refleja el nivel de activación del sistema nervioso de descanso y reparación (PNS). Valores positivos altos son favorables.",
      demoVal: 1.1,
    },
    {
      key: "ansIndexSNS",
      label: "Tono Simpático",
      unit: "",
      icon: Flame,
      explanation: "Refleja la activación del sistema nervioso de acción y estrés (SNS). Niveles elevados prolongados indican sobreesfuerzo orgánico.",
      demoVal: 0.6,
    }
  ];

  const getBiomarkerValue = (key) => {
    if (isDemo) {
      const found = BIOMARKERS_CONFIG.find(b => b.key === key);
      return found ? found.demoVal : null;
    }
    const val = latestMeasurement?.fields?.[key];
    if (val !== undefined && val !== null) return val;
    const profVal = profile?.[key];
    if (profVal !== undefined && profVal !== null) return profVal;
    if (key === "oximetria") {
      return profile?.oximetria || latestMeasurement?.fields?.oximetry || latestMeasurement?.fields?.oxygenSaturation || profile?.oxygenSaturation || latestMeasurement?.fields?.oximetria;
    }
    if (key === "heartRate") {
      return latestMeasurement?.fields?.heartRate || profile?.heartRate;
    }
    if (key === "heartRateVariability") {
      return latestMeasurement?.fields?.heartRateVariability || profile?.heartRateVariability || latestMeasurement?.fields?.hrv || profile?.hrv;
    }
    if (key === "respirationRate") {
      return latestMeasurement?.fields?.respirationRate || profile?.respirationRate || latestMeasurement?.fields?.respiratoryRate || profile?.respiratoryRate;
    }
    return null;
  };

  const getStressChartData = () => {
    const stressScans = measurementsHistory
      .filter(m => m.fields?.stressIndex != null)
      .slice(0, 7)
      .reverse();

    return stressScans.map(m => {
      const val = parseFloat(m.fields.stressIndex);
      const pct = Math.min(100, Math.max(5, Math.round((val / 1000) * 100)));
      let dayLabel = "Scan";
      if (m.createdAt) {
        try {
          let date = null;
          if (typeof m.createdAt.toDate === "function") {
            date = m.createdAt.toDate();
          } else if (m.createdAt.seconds) {
            date = new Date(m.createdAt.seconds * 1000);
          } else {
            date = new Date(m.createdAt);
          }
          if (date && !isNaN(date.getTime())) {
            const daysOfWeek = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
            const dayName = daysOfWeek[date.getDay()];
            const dayNum = date.getDate();
            dayLabel = `${dayName} ${dayNum}`;
          }
        } catch (e) {
          console.error("Error formatting date:", e);
        }
      }
      let statusText = "Óptimo";
      if (val >= 500) statusText = "Crítico";
      else if (val >= 300) statusText = "Alto";
      else if (val >= 150) statusText = "Moderado";
      return {
        day: dayLabel,
        val: pct,
        hrs: `${Math.round(val)} (${statusText})`,
      };
    });
  };

  const getBiomarkerGaugeConfig = (key, value) => {
    if (value === null || value === undefined) return null;
    const numVal = parseFloat(value);
    if (isNaN(numVal)) return null;

    const COLOR_GREEN = "#10B981";  // Bueno (esmeralda)
    const COLOR_YELLOW = "#F59E0B"; // Regular (ámbar)
    const COLOR_RED = "#EF4444";    // Alerta (rojo)

    let statusLabel = "Bueno";
    let statusColor = COLOR_GREEN;
    let markerPosition = 50;
    let segments = [];

    const helperMap = (val, inMin, inMax, outMin, outMax) => {
      if (inMax === inMin) return outMin;
      const pct = (val - inMin) / (inMax - inMin);
      return Math.max(outMin, Math.min(outMax, outMin + pct * (outMax - outMin)));
    };

    if (key === "heartRate") {
      segments = [
        { color: COLOR_YELLOW, width: "20%", label: "Bajo", range: "< 60 lpm" },
        { color: COLOR_GREEN, width: "60%", label: "Normal", range: "60 - 100 lpm" },
        { color: COLOR_RED, width: "20%", label: "Alto", range: "> 100 lpm" }
      ];
      if (numVal < 60) {
        statusLabel = numVal < 50 ? "Alerta (Bajo)" : "Regular (Bajo)";
        statusColor = numVal < 50 ? COLOR_RED : COLOR_YELLOW;
        markerPosition = helperMap(numVal, 40, 60, 5, 20);
      } else if (numVal <= 100) {
        statusLabel = "Bueno";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 60, 100, 20, 80);
      } else {
        statusLabel = numVal > 110 ? "Alerta (Alto)" : "Regular (Alto)";
        statusColor = numVal > 110 ? COLOR_RED : COLOR_YELLOW;
        markerPosition = helperMap(numVal, 100, 140, 80, 95);
      }
    }
    else if (key === "paSistolica") {
      segments = [
        { color: COLOR_GREEN, width: "45%", label: "Bueno", range: "< 120 mmHg" },
        { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "120 - 139 mmHg" },
        { color: COLOR_RED, width: "20%", label: "Alerta", range: ">= 140 mmHg" }
      ];
      if (numVal < 120) {
        statusLabel = "Bueno";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 90, 120, 5, 45);
      } else if (numVal < 140) {
        statusLabel = "Regular";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 120, 140, 45, 80);
      } else {
        statusLabel = "Alerta";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, 140, 180, 80, 95);
      }
    }
    else if (key === "paDiastolica") {
      segments = [
        { color: COLOR_GREEN, width: "45%", label: "Bueno", range: "< 80 mmHg" },
        { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "80 - 89 mmHg" },
        { color: COLOR_RED, width: "20%", label: "Alerta", range: ">= 90 mmHg" }
      ];
      if (numVal < 80) {
        statusLabel = "Bueno";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 50, 80, 5, 45);
      } else if (numVal < 90) {
        statusLabel = "Regular";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 80, 90, 45, 80);
      } else {
        statusLabel = "Alerta";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, 90, 110, 80, 95);
      }
    }
    else if (key === "oximetria") {
      segments = [
        { color: COLOR_RED, width: "30%", label: "Alerta", range: "< 92%" },
        { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "92% - 94%" },
        { color: COLOR_GREEN, width: "40%", label: "Bueno", range: ">= 95%" }
      ];
      if (numVal < 92) {
        statusLabel = "Alerta";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, 85, 92, 5, 30);
      } else if (numVal < 95) {
        statusLabel = "Regular";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 92, 95, 30, 60);
      } else {
        statusLabel = "Bueno";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 95, 100, 60, 95);
      }
    }
    else if (key === "respirationRate") {
      segments = [
        { color: COLOR_YELLOW, width: "20%", label: "Bajo", range: "< 12 rpm" },
        { color: COLOR_GREEN, width: "60%", label: "Normal", range: "12 - 20 rpm" },
        { color: COLOR_RED, width: "20%", label: "Alto", range: "> 20 rpm" }
      ];
      if (numVal < 12) {
        statusLabel = numVal < 10 ? "Alerta (Bajo)" : "Regular (Bajo)";
        statusColor = numVal < 10 ? COLOR_RED : COLOR_YELLOW;
        markerPosition = helperMap(numVal, 8, 12, 5, 20);
      } else if (numVal <= 20) {
        statusLabel = "Bueno";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 12, 20, 20, 80);
      } else {
        statusLabel = numVal > 24 ? "Alerta (Alto)" : "Regular (Alto)";
        statusColor = numVal > 24 ? COLOR_RED : COLOR_YELLOW;
        markerPosition = helperMap(numVal, 20, 28, 80, 95);
      }
    }
    else if (key === "heartRateVariability") {
      segments = [
        { color: COLOR_RED, width: "30%", label: "Alerta", range: "< 30 ms" },
        { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "30 - 50 ms" },
        { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "> 50 ms" }
      ];
      if (numVal < 30) {
        statusLabel = "Alerta";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, 10, 30, 5, 30);
      } else if (numVal <= 50) {
        statusLabel = "Regular";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 30, 50, 30, 60);
      } else {
        statusLabel = "Bueno";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 50, 110, 60, 95);
      }
    }
    else if (key === "stressIndex") {
      segments = [
        { color: COLOR_GREEN, width: "40%", label: "Óptimo", range: "< 150" },
        { color: COLOR_YELLOW, width: "30%", label: "Moderado", range: "150 - 300" },
        { color: COLOR_RED, width: "30%", label: "Estrés Alto", range: ">= 300" }
      ];
      if (numVal < 150) {
        statusLabel = "Óptimo";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 0, 150, 5, 40);
      } else if (numVal < 300) {
        statusLabel = "Moderado";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 150, 300, 40, 70);
      } else {
        statusLabel = numVal >= 500 ? "Crítico (Alto)" : "Estrés Alto";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, 300, 800, 70, 95);
      }
    }
    else if (key === "cardiovascularAge") {
      segments = [
        { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "<= edad real" },
        { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "+1 a +5 años" },
        { color: COLOR_RED, width: "30%", label: "Alerta", range: ">= +6 años" }
      ];
      const diff = numVal - userAge;
      if (diff <= 0) {
        statusLabel = "Bueno";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(diff, -10, 0, 5, 40);
      } else if (diff <= 5) {
        statusLabel = "Regular";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(diff, 0, 5, 40, 70);
      } else {
        statusLabel = "Alerta";
        statusColor = COLOR_RED;
        markerPosition = helperMap(diff, 5, 15, 70, 95);
      }
    }
    else if (key === "cvdRisk") {
      segments = [
        { color: COLOR_GREEN, width: "40%", label: "Bueno", range: "< 5%" },
        { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "5% - 10%" },
        { color: COLOR_RED, width: "30%", label: "Alerta", range: "> 10%" }
      ];
      if (numVal < 5) {
        statusLabel = "Óptimo";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 0, 5, 5, 40);
      } else if (numVal <= 10) {
        statusLabel = "Moderado";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 5, 10, 40, 70);
      } else {
        statusLabel = "Alerta";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, 10, 20, 70, 95);
      }
    }
    else if (key === "wellnessScore") {
      const val100 = numVal <= 10 ? numVal * 10 : numVal;
      segments = [
        { color: COLOR_RED, width: "35%", label: "Alerta", range: "< 50%" },
        { color: COLOR_YELLOW, width: "35%", label: "Regular", range: "50% - 79%" },
        { color: COLOR_GREEN, width: "30%", label: "Bueno", range: ">= 80%" }
      ];
      if (val100 < 50) {
        statusLabel = "Alerta";
        statusColor = COLOR_RED;
        markerPosition = helperMap(val100, 0, 50, 5, 35);
      } else if (val100 < 80) {
        statusLabel = "Regular";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(val100, 50, 80, 35, 70);
      } else {
        statusLabel = "Excelente";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(val100, 80, 100, 70, 95);
      }
    }
    else if (key === "cardiovascularBMI") {
      segments = [
        { color: COLOR_YELLOW, width: "25%", label: "Bajo", range: "< 18.5" },
        { color: COLOR_GREEN, width: "40%", label: "Normal", range: "18.5 - 24.9" },
        { color: COLOR_RED, width: "35%", label: "Sobrepeso+", range: ">= 25" }
      ];
      if (numVal < 18.5) {
        statusLabel = "Bajo peso";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 10, 18.5, 5, 25);
      } else if (numVal <= 24.9) {
        statusLabel = "Óptimo";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 18.5, 24.9, 25, 65);
      } else {
        statusLabel = numVal >= 30 ? "Alerta (Obesidad)" : "Sobrepeso";
        statusColor = numVal >= 30 ? COLOR_RED : COLOR_YELLOW;
        markerPosition = helperMap(numVal, 24.9, 40, 65, 95);
      }
    }
    else if (key === "ansIndexPNS") {
      segments = [
        { color: COLOR_RED, width: "30%", label: "Suprimido", range: "< -2" },
        { color: COLOR_YELLOW, width: "30%", label: "Regular", range: "-2 a 0" },
        { color: COLOR_GREEN, width: "40%", label: "Óptimo", range: ">= 0" }
      ];
      if (numVal < -2) {
        statusLabel = "Suprimido";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, -5, -2, 5, 30);
      } else if (numVal < 0) {
        statusLabel = "Moderado";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, -2, 0, 30, 60);
      } else {
        statusLabel = "Excelente";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, 0, 5, 60, 95);
      }
    }
    else if (key === "ansIndexSNS") {
      segments = [
        { color: COLOR_GREEN, width: "40%", label: "Relajado", range: "< 1.5" },
        { color: COLOR_YELLOW, width: "30%", label: "Moderado", range: "1.5 a 3.0" },
        { color: COLOR_RED, width: "30%", label: "Estrés", range: ">= 3.0" }
      ];
      if (numVal < 1.5) {
        statusLabel = "Relajado";
        statusColor = COLOR_GREEN;
        markerPosition = helperMap(numVal, -5, 1.5, 5, 40);
      } else if (numVal < 3) {
        statusLabel = "Tensión leve";
        statusColor = COLOR_YELLOW;
        markerPosition = helperMap(numVal, 1.5, 3, 40, 70);
      } else {
        statusLabel = "Estrés elevado";
        statusColor = COLOR_RED;
        markerPosition = helperMap(numVal, 3, 5, 70, 95);
      }
    }

    return {
      statusLabel,
      statusColor,
      markerPosition,
      segments
    };
  };

  const hasHeartRate = latestMeasurement?.fields?.heartRate || profile?.heartRate;
  const heartRateValue = hasHeartRate ? (latestMeasurement?.fields?.heartRate || profile?.heartRate) : (isDemo ? 72 : null);

  const hasHrv = latestMeasurement?.fields?.heartRateVariability || profile?.heartRateVariability || latestMeasurement?.fields?.hrv || profile?.hrv;
  const hrvValue = hasHrv ? (latestMeasurement?.fields?.heartRateVariability || profile?.heartRateVariability || latestMeasurement?.fields?.hrv || profile?.hrv) : (isDemo ? 48 : null);

  const hasSpO2 = profile.oximetria || latestMeasurement?.fields?.oximetry || latestMeasurement?.fields?.oxygenSaturation || profile?.oxygenSaturation || latestMeasurement?.fields?.oximetria;
  const spo2Value = hasSpO2 ? (profile.oximetria || latestMeasurement?.fields?.oximetry || latestMeasurement?.fields?.oxygenSaturation || profile?.oxygenSaturation || latestMeasurement?.fields?.oximetria) : (isDemo ? 98 : null);

  const hasTemp = latestMeasurement?.fields?.temperature || profile?.temperature;
  const tempValue = hasTemp ? (latestMeasurement?.fields?.temperature || profile?.temperature) : (isDemo ? 36.6 : null);

  const hasRespRate = latestMeasurement?.fields?.respirationRate || profile?.respirationRate || latestMeasurement?.fields?.respiratoryRate || profile?.respiratoryRate;
  const respRateValue = hasRespRate ? (latestMeasurement?.fields?.respirationRate || profile?.respirationRate || latestMeasurement?.fields?.respiratoryRate || profile?.respiratoryRate) : (isDemo ? 14 : null);

  const hasGlucose = profile.glucemia || latestMeasurement?.fields?.glucose || latestMeasurement?.fields?.glucemia || profile?.glucose;
  const glucoseValue = hasGlucose ? (profile.glucemia || latestMeasurement?.fields?.glucose || latestMeasurement?.fields?.glucemia || profile?.glucose) : (isDemo ? 92 : null);

  return (
    <div className="min-h-screen relative overflow-x-hidden text-[#1E1B16] font-sans pb-10" style={{ background: "#FCFAEE" }}>
      <style>{`
        .we360-ecg-animation {
          stroke-dasharray: 1000;
          stroke-dashoffset: 1000;
          animation: ecgDash 8s linear infinite;
        }
        @keyframes ecgDash {
          to {
            stroke-dashoffset: 0;
          }
        }
        .animate-spin-slow {
          animation: spin 10s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fabFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-fab-float {
          animation: fabFloat 3s ease-in-out infinite;
        }
      `}</style>

      {/* Ambient Background Glows */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none -z-10">
        <div className="absolute top-[-20%] left-[-10%] w-[800px] h-[800px] rounded-full bg-[#2A9D8F] opacity-5 blur-[120px]"></div>
        <div className="absolute bottom-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#00628F] opacity-5 blur-[100px]"></div>
      </div>

      <div className="flex">
        {/* ── Desktop Sidebar ── */}
        <nav className="hidden md:flex flex-col h-screen py-8 px-6 w-64 fixed left-0 top-0 border-r border-white/20 bg-white/70 backdrop-blur-[20px] shadow-[0_12px_40px_rgba(0,0,0,0.05)] z-50">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00628F] to-[#2A9D8F] flex items-center justify-center text-white font-bold text-lg shadow-sm">W</div>
            <div>
              <h1 className="text-xl font-extrabold text-[#00628F] tracking-tight">WE360</h1>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Premium Mode</p>
            </div>
          </div>

          <div className="flex-1 flex flex-col gap-1.5">
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#00628F] font-bold bg-[#00628F]/10 text-left transition-all">
              <Activity size={18} />
              <span className="text-sm">Dashboard</span>
            </button>
            <button onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-100/50 text-left transition-colors">
              <User size={18} />
              <span className="text-sm">Mi Cuenta</span>
            </button>
            {hasF1 && (
              <button onClick={() => navigate("/medicion/nueva")} className="flex items-center gap-3 px-4 py-3 rounded-xl text-slate-600 hover:bg-slate-100/50 text-left transition-colors">
                <Camera size={18} />
                <span className="text-sm">Nueva Medición</span>
              </button>
            )}
          </div>

          <div className="mt-auto pt-6 border-t border-slate-200/50 flex flex-col gap-3">
            {/* Toggle View Mode Button in Sidebar */}
            <button
              onClick={toggleViewMode}
              className="w-full py-2.5 rounded-xl border border-[#00628F]/20 bg-white/60 hover:bg-white transition-all text-xs font-bold text-[#00628F] flex items-center justify-center gap-2"
              data-testid="toggle-view-classic-sidebar"
            >
              <RefreshCw size={13} className="animate-spin-slow text-[#2A9D8F]" />
              Ver Versión Clásica
            </button>
          </div>
        </nav>

        {/* ── Main Panel ── */}
        <div className="flex-1 md:ml-64 min-h-screen flex flex-col">
          {/* Top Header */}
          <header className="flex justify-between items-center w-full px-6 py-4 sticky top-0 z-40 bg-[#FCFAEE]/80 backdrop-blur-md border-b border-slate-100">
            <div className="flex items-center gap-3 md:hidden">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#00628F] to-[#2A9D8F] flex items-center justify-center text-white font-bold text-sm shadow-sm">W</div>
              <span className="font-black text-[#00628F] tracking-tight">WE360</span>
            </div>

            <div className="hidden md:block">
              <h2 className="text-sm font-bold text-slate-700">Bienvenido de vuelta, {profile.nombres || "Usuario"}</h2>
            </div>

            <div className="flex items-center gap-3">
              {/* Header Action: Toggle View */}
              <button
                onClick={toggleViewMode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#00628F]/30 bg-[#00628F]/5 hover:bg-[#00628F]/10 transition-colors text-xs font-bold text-[#00628F]"
                data-testid="toggle-view-classic"
              >
                <RefreshCw size={13} className="text-[#2A9D8F]" />
                <span className="hidden sm:inline">Modo Clásico</span>
              </button>

              <div className="flex items-center gap-2 bg-white/50 backdrop-blur-md px-3 py-1 rounded-full border border-white/50 shadow-sm">
                <div className="w-2 h-2 bg-[#2A9D8F] rounded-full animate-ping"></div>
                <span className="text-[11px] font-bold text-[#2A9D8F]">Telemetría Activa</span>
              </div>

              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00628F] to-[#2A9D8F] flex items-center justify-center text-white font-bold text-xs shadow-sm">
                {hasF1 ? profile.nombres?.[0]?.toUpperCase() : "W"}
              </div>
            </div>
          </header>

          {/* Page Canvas */}
          <div className="flex-1 p-4 md:p-8 max-w-[1200px] w-full mx-auto space-y-6">

            {/* ═══ STICKY CRITICAL ALERT — siempre primero, máxima visibilidad ═══ */}
            {criticalAlerts.length > 0 && !stickyDismissed && (
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl we360-pulse-border"
                style={{ background: "#FFF0F0", border: "2px solid #C0453A" }}
                data-testid="dashboard-sticky-critical"
              >
                <Siren size={16} color="#9B1C1C" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold" style={{ color: "#9B1C1C" }}>
                    {criticalAlerts.length} alerta{criticalAlerts.length > 1 ? "s" : ""} de atención inmediata
                  </p>
                  <p className="text-[11px]" style={{ color: "#C0453A" }}>
                    Consulta con un médico a la brevedad posible
                  </p>
                </div>
                <button
                  onClick={() => setShowCriticalModal(true)}
                  className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
                  style={{ background: "rgba(192,69,58,0.12)", color: "#9B1C1C", border: "none", cursor: "pointer" }}
                  data-testid="dashboard-sticky-close"
                  aria-label="Cerrar alerta"
                >
                  <X size={14} strokeWidth={2.5} />
                </button>
              </div>
            )}

            {/* ═══ PAR-Q — Autorización médica requerida (acción requerida) ═══ */}
            {needsParqConsent && (
              <div
                className="flex items-start gap-3 px-4 py-3.5 rounded-xl"
                style={{ background: "#FEF3C7", border: "2px solid #FCD34D" }}
                data-testid="dashboard-parq-banner"
              >
                <AlertOctagon size={18} color="#D97706" className="shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold" style={{ color: "#92400E" }}>
                    Acción requerida: autorización médica
                  </p>
                  <p className="text-[11px] leading-relaxed mb-2.5" style={{ color: "#78350F" }}>
                    Detectamos {parqRiskCount} {parqRiskCount === 1 ? "factor de riesgo" : "factores de riesgo"} en
                    tu evaluación física (PAR-Q). Sube tu autorización médica para poder
                    registrar mediciones de forma segura.
                  </p>
                  <button
                    onClick={() => setShowParqModal(true)}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white transition-transform active:scale-[0.98]"
                    style={{ background: "linear-gradient(135deg,#00628F 0%,#0080A0 50%,#2A9D8F 100%)", border: "none", cursor: "pointer" }}
                    data-testid="dashboard-parq-cta"
                  >
                    <ClipboardList size={14} /> Resolver autorización
                  </button>
                </div>
              </div>
            )}

            {/* ── Banner de riesgo: B2B sin email personal ── */}
            {clientId && !personalEmail && (
              <div
                className="flex items-start gap-3 px-4 py-3 rounded-xl cursor-pointer"
                style={{ background: "#FFFBEB", border: "1px solid #FCD34D" }}
                onClick={() => navigate("/mi-cuenta")}
                data-testid="dashboard-risk-banner"
              >
                <span className="text-base shrink-0" style={{ lineHeight: 1 }}>⚠️</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold" style={{ color: "#92400E" }}>
                    Protege tu historial de salud
                  </p>
                  <p className="text-[11px]" style={{ color: "#B45309" }}>
                    Tu acceso depende de tu cuenta corporativa. Agrega tu email personal en Mi Cuenta →
                  </p>
                </div>
              </div>
            )}

            {/* ── Banner opt-in Push Notifications ── */}
            {showPushBanner && (
              <div
                className="flex items-center gap-3 px-4 py-3 rounded-xl"
                style={{ background: "#EFF6FF", border: "1px solid #BFDBFE" }}
                data-testid="dashboard-push-banner"
              >
                <Bell size={16} color="#1D4ED8" className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold" style={{ color: "#1E40AF" }}>Activar alertas en tiempo real</p>
                  <p className="text-[11px]" style={{ color: "#3B82F6" }}>
                    Recibe notificaciones cuando tus mediciones sean críticas, aunque la app esté cerrada.
                  </p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    onClick={() => { subscribePush(); }}
                    disabled={pushLoading}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-lg"
                    style={{ background: "#1D4ED8", color: "white", border: "none", cursor: "pointer" }}
                    data-testid="push-subscribe-btn"
                  >
                    {pushLoading ? "..." : "Activar"}
                  </button>
                  <button
                    onClick={() => { localStorage.setItem("we360_push_dismissed","1"); setPushBannerDismissed(true); }}
                    className="text-[11px] px-2 py-1 rounded-lg"
                    style={{ background: "transparent", color: "#93C5FD", border: "1px solid #BFDBFE", cursor: "pointer" }}
                    data-testid="push-dismiss-btn"
                  >
                    Ahora no
                  </button>
                </div>
              </div>
            )}

            {/* Toast saved status */}
            {savedToast && (
              <div
                className="rounded-xl px-3 py-2.5 flex items-center gap-2"
                style={{
                  background: "#E2F0E8",
                  border: "1px solid rgba(21,115,70,0.2)",
                }}
                data-testid="dashboard-measurement-saved-toast"
              >
                <CheckCircle size={14} color="#157346" />
                <span className="text-xs font-semibold flex-1" style={{ color: "#157346" }}>
                  {savedToast === "camera"
                    ? "Escaneo facial guardado — revísalo en Historial de mediciones"
                    : "Medición guardada correctamente"}
                </span>
                <button onClick={() => setSavedToast(null)} className="p-0.5 hover:opacity-60 transition-opacity cursor-pointer">
                  <X size={12} color="#157346" />
                </button>
              </div>
            )}

            {/* ═══ MODAL — Subir autorización médica (PAR-Q) inline ═══ */}
            {showParqModal && (
              <div
                className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6 overflow-y-auto"
                data-testid="dashboard-parq-modal"
              >
                <div className="w-full max-w-md my-auto">
                  <div className="flex justify-end mb-2">
                    <button
                      onClick={() => setShowParqModal(false)}
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ background: "rgba(255,255,255,0.9)", border: "none", cursor: "pointer" }}
                      data-testid="dashboard-parq-modal-close"
                      aria-label="Cerrar"
                    >
                      <X size={16} color="#1E293B" />
                    </button>
                  </div>
                  <PARQConsentGate riskCount={parqRiskCount} onResolved={handleParqResolved} />
                </div>
              </div>
            )}

            {/* ═══ MODAL — Importancia de atención médica ═══ */}
            {showCriticalModal && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center px-4"
                style={{ background: "rgba(0,0,0,0.55)" }}
                onClick={() => setShowCriticalModal(false)}
                data-testid="critical-modal-overlay"
              >
                <div
                  className="w-full max-w-[380px] rounded-2xl p-5"
                  style={{ background: "white", boxShadow: "0 20px 50px rgba(0,0,0,0.25)" }}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="critical-modal"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                    style={{ background: "#FEE2E2" }}
                  >
                    <AlertOctagon size={24} color="#9B1C1C" />
                  </div>
                  <h3 className="text-base font-bold mb-2" style={{ color: "#1F2937" }}>
                    Esto no es una alerta cualquiera
                  </h3>
                  <p className="text-sm leading-relaxed mb-3" style={{ color: "#4B5563" }}>
                    Detectamos {criticalAlerts.length === 1 ? "un valor" : `${criticalAlerts.length} valores`} en tus mediciones que {criticalAlerts.length === 1 ? "supera" : "superan"} los <strong>umbrales clínicos críticos</strong> definidos por la AHA/ACC, ADA y OMS.
                  </p>

                  <div className="rounded-xl p-3 mb-3" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
                    <p className="text-xs font-semibold mb-2" style={{ color: "#991B1B" }}>
                      Qué detectamos en tu caso:
                    </p>
                    <ul className="space-y-2">
                      {criticalAlerts.map((a, i) => {
                        const info = getCriticalInfo(a);
                        return (
                          <li key={i} className="flex items-start gap-2" data-testid={`critical-modal-item-${i}`}>
                            <span className="text-[14px] leading-none mt-0.5" style={{ color: "#C0453A" }}>•</span>
                            <div className="flex-1 min-w-0">
                              <p className="text-[13px] font-semibold" style={{ color: "#7C2D12" }}>
                                {info.label}: {formatAlertValue(a)}
                              </p>
                              <p className="text-[11px]" style={{ color: "#9A3412" }}>
                                Sistema {info.system}
                              </p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  <div className="rounded-xl p-3 mb-3" style={{ background: "#FFF7ED", border: "1px solid #FED7AA" }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: "#9A3412" }}>
                      ¿Por qué es importante actuar ahora?
                    </p>
                    <ul className="text-[12px] space-y-1.5" style={{ color: "#7C2D12" }}>
                      <li>• Estos valores se asocian a <strong>complicaciones evitables</strong> si se tratan a tiempo.</li>
                      <li>• Una consulta médica oportuna permite <strong>confirmar el diagnóstico</strong> y prevenir un evento serio.</li>
                      <li>• WE360 detecta señales, pero el diagnóstico y tratamiento los hace tu médico.</li>
                    </ul>
                  </div>
                  <p className="text-[11px] mb-4 leading-relaxed" style={{ color: "#6B7280" }}>
                    Si cierras este aviso, la alerta seguirá visible en la sección &ldquo;Atención inmediata&rdquo; más abajo. Te recomendamos agendar una consulta médica en las próximas 24 horas.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setShowCriticalModal(false)}
                      className="flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold transition-colors hover:bg-slate-100"
                      style={{ background: "#F1F5F9", color: "#475569", border: "1px solid #E2D9CB", cursor: "pointer" }}
                      data-testid="critical-modal-keep"
                    >
                      Mantener visible
                    </button>
                    <button
                      onClick={() => { setStickyDismissed(true); setShowCriticalModal(false); }}
                      className="flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold"
                      style={{ background: "#C0453A", color: "white", border: "none", cursor: "pointer" }}
                      data-testid="critical-modal-dismiss"
                    >
                      Entiendo, cerrar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ═══ BENTO GRID ═══ */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="dashboard-hero">
              {/* 1. Score de Salud (8 cols) */}
              <div className="md:col-span-8 bg-white/60 backdrop-blur-md border border-white/20 p-6 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
                <div className="flex-1 space-y-4">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#2A9D8F] bg-[#2A9D8F]/10 px-2.5 py-1 rounded-md">Puntuación Integral</span>
                  <h3 className="text-2xl font-extrabold text-[#00628F] leading-tight" data-testid="dashboard-title">Tu Score de Longevidad</h3>
                  <p className="text-xs text-slate-500 leading-relaxed max-w-md">
                    {allComplete 
                      ? "Tu puntuación de bienestar general está completamente optimizada y se basa en tus chequeos clínicos y de estilo de vida."
                      : `Has completado ${completedCount} de 5 evaluaciones. Completa las restantes para calibrar tu puntuación real.`}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {allComplete ? (
                      <button 
                        onClick={() => navigate("/recommendations")}
                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white text-xs font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center gap-1.5"
                        data-testid="dashboard-recommendations-cta-score"
                      >
                        <Sparkles size={14} /> Ver Plan Personalizado
                      </button>
                    ) : (
                      <button 
                        onClick={() => navigate(firstIncompleteForm)}
                        className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white text-xs font-bold shadow-md hover:shadow-lg active:scale-95 transition-all flex items-center gap-1.5"
                        data-testid="dashboard-start-btn"
                      >
                        Continuar Evaluaciones <ArrowRight size={14} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Circular Progress Ring */}
                <div className="relative w-36 h-36 flex items-center justify-center shrink-0" data-testid="dashboard-completion-badge">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                    <circle cx="50" cy="50" fill="none" r="42" stroke="#E2D9CB" strokeWidth="6" opacity="0.3"></circle>
                    <circle 
                      cx="50" 
                      cy="50" 
                      fill="none" 
                      r="42" 
                      stroke="url(#premiumTealGrad)" 
                      strokeDasharray="263.89" 
                      strokeDashoffset={263.89 - (263.89 * longevityScore) / 100} 
                      strokeLinecap="round" 
                      strokeWidth="7"
                      className="transition-all duration-1000 ease-out"
                    ></circle>
                    <defs>
                      <linearGradient id="premiumTealGrad" x1="0%" x2="100%" y1="0%" y2="100%">
                        <stop offset="0%" stopColor="#00628F"></stop>
                        <stop offset="100%" stopColor="#2A9D8F"></stop>
                      </linearGradient>
                    </defs>
                  </svg>
                  <div className="absolute text-center">
                    <span className="block text-3xl font-black text-[#00628F] leading-none">
                      {longevityScore}
                    </span>
                    <span className="block text-[9px] text-slate-400 font-bold uppercase tracking-widest mt-1">/100</span>
                  </div>
                </div>
              </div>

              {/* 2. Ritmo Cardíaco (4 cols) */}
              <div className="md:col-span-4 bg-white/60 backdrop-blur-md border border-white/20 p-6 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col justify-between min-h-[220px]">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2.5">
                    <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center text-red-500 shadow-sm">
                      <Heart size={20} fill="currentColor" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-[#00628F]">Ritmo Cardíaco</h3>
                      <p className="text-[10px] text-slate-400 font-medium">Fisiología activa</p>
                    </div>
                  </div>
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border ${
                    heartRateValue !== null 
                      ? "bg-[#2A9D8F]/10 text-[#2A9D8F] border-[#2A9D8F]/20" 
                      : "bg-slate-100 text-slate-400 border-slate-200"
                  }`}>
                    {heartRateValue !== null ? "Estable" : "Sin registros"}
                  </span>
                </div>
                <div className="mt-4">
                  <div className="flex items-baseline gap-1">
                    <span className={heartRateValue !== null ? "text-4xl font-extrabold text-[#00628F]" : "text-2xl font-extrabold text-[#00628F]/60"}>
                      {heartRateValue !== null ? heartRateValue : "Sin datos"}
                    </span>
                    {heartRateValue !== null && (
                      <span className="text-xs text-slate-400 font-bold">lpm</span>
                    )}
                  </div>
                  {/* Beautiful Animated SVG wave */}
                  <div className="h-12 w-full mt-3 overflow-hidden relative">
                    <svg className={`w-full h-full ${heartRateValue !== null ? "stroke-[#2A9D8F]" : "stroke-slate-300"} fill-none`} preserveAspectRatio="none" viewBox="0 0 200 40">
                      {heartRateValue !== null ? (
                        <path d="M0,20 L20,20 L30,20 L35,5 L40,35 L45,15 L50,25 L55,20 L75,20 L80,5 L85,35 L90,15 L95,25 L100,20 L150,20 L155,5 L160,35 L165,15 L170,25 L175,20 L200,20" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" className="we360-ecg-animation"></path>
                      ) : (
                        <path d="M0,20 L200,20" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                      )}
                    </svg>
                  </div>
                </div>
              </div>

              {/* 3. HRV Card (4 cols) */}
              <div className="md:col-span-4 bg-white/60 backdrop-blur-md border border-white/20 p-6 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col justify-between min-h-[220px]">
                <div className="flex items-center gap-2.5">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-[#2A9D8F] shadow-sm">
                    <Activity size={20} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#00628F]">Variabilidad (HRV)</h3>
                    <p className="text-[10px] text-slate-400 font-medium">Estrés del sistema autónomo</p>
                  </div>
                </div>
                <div className="mt-4">
                  <div className="flex items-baseline gap-1">
                    <span className={hrvValue !== null ? "text-4xl font-extrabold text-[#00628F]" : "text-2xl font-extrabold text-[#00628F]/60"}>
                      {hrvValue !== null ? hrvValue : "Sin datos"}
                    </span>
                    {hrvValue !== null && (
                      <span className="text-xs text-slate-400 font-bold">ms</span>
                    )}
                  </div>
                  {/* Position gauge slider bar */}
                  <div className="mt-4 space-y-2">
                    <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden relative">
                      <div 
                        className="absolute top-0 h-full bg-gradient-to-r from-[#00628F] to-[#2A9D8F] rounded-full transition-all duration-1000"
                        style={{ width: `${hrvValue !== null ? Math.min(100, Math.max(15, (hrvValue / 100) * 100)) : 0}%` }}
                      ></div>
                    </div>
                    <div className={`flex justify-between text-[10px] ${hrvValue !== null ? "text-slate-400" : "text-slate-300"} font-bold uppercase tracking-wider`}>
                      <span>Bajo</span>
                      <span>Óptimo</span>
                      <span>Excelente</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* 4. Timeline Card (8 cols) */}
              <div className="md:col-span-8 bg-white/60 backdrop-blur-md border border-white/20 p-6 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col justify-between min-h-[220px]" data-testid="dashboard-form-status">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-sm font-bold text-[#00628F]">Ruta de Chequeos Activos</h3>
                    <p className="text-[10px] text-slate-400 font-medium">Progreso de tus 5 evaluaciones principales</p>
                  </div>
                  <span className="text-xs font-bold text-[#00628F] bg-[#00628F]/10 px-2.5 py-1 rounded-full">{completedCount}/5 Completados</span>
                </div>

                {/* Beautiful Horizontal Timeline */}
                <div className="relative flex justify-between items-center px-4 py-3 mt-4">
                  {/* Background Connector Bar */}
                  <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2 h-1 bg-slate-100 z-0"></div>
                  <div 
                    className="absolute left-6 top-1/2 -translate-y-1/2 h-1 bg-gradient-to-r from-[#00628F] to-[#2A9D8F] z-0 transition-all duration-700"
                    style={{ width: `${completedCount === 0 ? 0 : completedCount === 5 ? 100 : ((completedCount - 1) / 4) * 100}%` }}
                  ></div>

                  {[
                    { n: 1, label: "F1", done: hasF1, active: !hasF1, title: "Demográfico y Clínico", path: "/f1" },
                    { n: 2, label: "F2", done: hasF2, active: hasF1 && !hasF2, title: "Prueba Física", path: "/f2" },
                    { n: 3, label: "F3", done: hasF3, active: hasF2 && !hasF3, title: "Actividad Física", path: "/f3" },
                    { n: 4, label: "F4", done: hasF4, active: hasF3 && !hasF4, title: "Nutrición", path: "/f4" },
                    { n: 5, label: "F5", done: hasF5, active: hasF4 && !hasF5, title: "Emocional", path: "/f5" },
                  ].map((f) => (
                    <button
                      key={f.n}
                      onClick={() => navigate(f.path)}
                      data-testid={`form-status-f${f.n}`}
                      className="relative z-10 flex flex-col items-center gap-1.5 focus:outline-none group active:scale-95 transition-transform"
                    >
                      <div 
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shadow-md transition-all duration-300 ${
                          f.done 
                            ? "bg-[#2A9D8F] text-white" 
                            : f.active 
                              ? "bg-white border-2 border-[#2A9D8F] text-[#2A9D8F] ring-4 ring-[#2A9D8F]/10 animate-pulse" 
                              : "bg-slate-100 text-slate-400 border border-slate-200"
                        }`}
                      >
                        {f.done ? <CheckCircle size={14} className="stroke-white" /> : f.label}
                      </div>
                      <span className={`text-[10px] font-bold ${f.done || f.active ? "text-[#00628F]" : "text-slate-400"}`}>
                        {f.label}
                      </span>
                    </button>
                  ))}
                </div>

                {/* Adaptive Information Banner */}
                <div className="mt-4 bg-[#EFF6FF] rounded-2xl p-4 flex items-center gap-3 border border-blue-100">
                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
                    <Clock size={16} />
                  </div>
                  <div className="flex-1">
                    {allComplete ? (
                      <>
                        <p className="text-xs text-blue-900 font-bold">¡Evaluaciones Completadas!</p>
                        <p className="text-[10px] text-blue-700 leading-snug">Ya tienes acceso ilimitado a tus recomendaciones personalizadas e historial.</p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs text-blue-900 font-bold">Próximo paso pendiente: {!hasF1 ? "F1" : !hasF2 ? "F2" : !hasF3 ? "F3" : !hasF4 ? "F4" : "F5"}</p>
                        <p className="text-[10px] text-blue-700 leading-snug">
                          Completa la evaluación para desbloquear tus predicciones y score clínicos interactivos.
                        </p>
                      </>
                    )}
                  </div>
                  {!allComplete && (
                    <button 
                      onClick={() => navigate(firstIncompleteForm)}
                      className="px-3.5 py-1.5 bg-gradient-to-r from-[#00628F] to-[#2A9D8F] hover:opacity-95 text-white text-[10px] font-bold rounded-lg shadow-sm whitespace-nowrap"
                    >
                      Comenzar
                    </button>
                  )}
                </div>
              </div>

              {/* ── Nivel de Acompañamiento Clínico (Triaje HI-OS - 12 cols) ── */}
              <div className="md:col-span-12 relative">
                <PersonalTriageCard
                  profile={profile}
                  latestMeasurement={latestMeasurement}
                  isDemo={isDemo}
                  onNavigateToMeasurement={() => navigate(firstIncompleteForm || "/measure")}
                  className="w-full"
                />
                {!isDemo && !allComplete && (
                  <div
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/75 backdrop-blur-md rounded-3xl p-6 text-center border border-slate-200/80 shadow-[inset_0_4px_30px_rgba(0,0,0,0.03)]"
                    data-testid="triage-card-locked-overlay"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#00628F]/10 border border-[#00628F]/20 flex items-center justify-center text-[#00628F] mb-3 shadow-xs">
                      <Lock size={22} />
                    </div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 mb-2">
                      {completedCount} de 5 formularios completados
                    </span>
                    <h4 className="text-sm sm:text-base font-extrabold text-[#00628F] mb-1.5">
                      Nivel de Acompañamiento Clínico Bloqueado
                    </h4>
                    <p className="text-xs text-slate-600 max-w-md leading-relaxed mb-4">
                      Completa las 5 evaluaciones integrales (F1 a F5) para consolidar tu triaje clínico y desbloquear tu ruta personalizada de bienestar.
                    </p>
                    <button
                      onClick={() => navigate(firstIncompleteForm)}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white font-bold text-xs shadow-sm hover:opacity-95 transition-all flex items-center gap-2"
                    >
                      <span>Continuar en {!hasF1 ? "F1" : !hasF2 ? "F2" : !hasF3 ? "F3" : !hasF4 ? "F4" : "F5"}</span>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* ── Radar Holístico de Longevidad (6 Dimensiones Clínicas - 12 cols) ── */}
              <div className="md:col-span-12 relative">
                <PersonalRadarCard
                  profile={profile}
                  latestMeasurement={latestMeasurement}
                  isDemo={isDemo}
                  onNavigateToMeasurement={() => navigate("/measure")}
                  className="w-full"
                />
                {!isDemo && !allComplete && (
                  <div
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/75 backdrop-blur-md rounded-3xl p-6 text-center border border-slate-200/80 shadow-[inset_0_4px_30px_rgba(0,0,0,0.03)]"
                    data-testid="radar-card-locked-overlay"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#00628F]/10 border border-[#00628F]/20 flex items-center justify-center text-[#00628F] mb-3 shadow-xs">
                      <Lock size={22} />
                    </div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 mb-2">
                      {completedCount} de 5 formularios completados
                    </span>
                    <h4 className="text-sm sm:text-base font-extrabold text-[#00628F] mb-1.5">
                      Radar Holístico de Longevidad Bloqueado
                    </h4>
                    <p className="text-xs text-slate-600 max-w-md leading-relaxed mb-4">
                      Este mapa multidimensional integra tus dimensiones clínica, física, de movimiento, nutricional y emocional al completar los 5 formularios.
                    </p>
                    <button
                      onClick={() => navigate(firstIncompleteForm)}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white font-bold text-xs shadow-sm hover:opacity-95 transition-all flex items-center gap-2"
                    >
                      <span>Continuar en {!hasF1 ? "F1" : !hasF2 ? "F2" : !hasF3 ? "F3" : !hasF4 ? "F4" : "F5"}</span>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* ── Benchmarking Poblacional & Percentil de Salud (12 cols) ── */}
              <div className="md:col-span-12 relative">
                <PersonalPopulationBenchmarkCard
                  profile={profile}
                  latestMeasurement={latestMeasurement}
                  isDemo={isDemo}
                  onNavigateToMeasurement={() => navigate(firstIncompleteForm || "/measure")}
                  className="w-full"
                />
                {!isDemo && !allComplete && (
                  <div
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/75 backdrop-blur-md rounded-3xl p-6 text-center border border-slate-200/80 shadow-[inset_0_4px_30px_rgba(0,0,0,0.03)]"
                    data-testid="benchmark-card-locked-overlay"
                  >
                    <div className="w-12 h-12 rounded-2xl bg-[#00628F]/10 border border-[#00628F]/20 flex items-center justify-center text-[#00628F] mb-3 shadow-xs">
                      <Lock size={22} />
                    </div>
                    <span className="text-[10px] font-extrabold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 mb-2">
                      {completedCount} de 5 formularios completados
                    </span>
                    <h4 className="text-sm sm:text-base font-extrabold text-[#00628F] mb-1.5">
                      Benchmarking Poblacional Bloqueado
                    </h4>
                    <p className="text-xs text-slate-600 max-w-md leading-relaxed mb-4">
                      Completa tus 5 evaluaciones de salud y bienestar para comparar tu percentil de longevidad frente a tu grupo demográfico.
                    </p>
                    <button
                      onClick={() => navigate(firstIncompleteForm)}
                      className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white font-bold text-xs shadow-sm hover:opacity-95 transition-all flex items-center gap-2"
                    >
                      <span>Continuar en {!hasF1 ? "F1" : !hasF2 ? "F2" : !hasF3 ? "F3" : !hasF4 ? "F4" : "F5"}</span>
                      <ArrowRight size={14} />
                    </button>
                  </div>
                )}
              </div>

              {/* Longevity Metrics Chart (12 cols) */}
              <div className="md:col-span-12 bg-white/60 backdrop-blur-md border border-white/20 p-6 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                  <div>
                    <h3 className="text-base font-bold text-[#00628F]">Métricas de Longevidad</h3>
                    <p className="text-[10px] text-slate-400 font-medium">Análisis semanal de biomarcadores clave</p>
                  </div>
                  <div className="flex gap-1.5 bg-slate-100/80 p-1 rounded-xl">
                    {[
                      { id: "sleep", label: "Sueño" },
                      { id: "stress", label: "Estrés" },
                      { id: "activity", label: "Actividad" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => setTrackerTab(tab.id)}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                          trackerTab === tab.id 
                            ? "bg-[#00628F] text-white shadow-sm" 
                            : "text-slate-500 hover:text-slate-800"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex-1 relative flex items-end w-full border-b border-slate-100 pb-4 h-48">
                  {/* Y-Axis */}
                  <div className="absolute left-0 top-0 bottom-4 w-8 flex flex-col justify-between items-end pr-2.5 font-bold text-[9px] text-slate-400 border-r border-slate-100/50">
                    <span>100%</span>
                    <span>75%</span>
                    <span>50%</span>
                    <span>25%</span>
                    <span>0%</span>
                  </div>

                  {/* Dynamic Chart Bars based on trackerTab */}
                  {(isDemo || (trackerTab === "stress" && measurementsHistory.some(m => m.fields?.stressIndex != null))) ? (
                    <div className="ml-10 flex-1 h-full flex items-end justify-around px-4 gap-2">
                      {(() => {
                        const data = isDemo
                          ? (trackerTab === "sleep" 
                            ? [
                                { day: "Lun", val: 80, hrs: "7.5h" },
                                { day: "Mar", val: 90, hrs: "8.2h" },
                                { day: "Mié", val: 70, hrs: "6.8h" },
                                { day: "Jue", val: 85, hrs: "7.8h" },
                                { day: "Vie", val: 60, hrs: "5.5h" },
                                { day: "Sáb", val: 95, hrs: "9.0h" },
                                { day: "Dom", val: 88, hrs: "8.0h" },
                              ]
                            : trackerTab === "stress"
                            ? [
                                { day: "Lun", val: 40, hrs: "Bajo" },
                                { day: "Mar", val: 50, hrs: "Medio" },
                                { day: "Mié", val: 30, hrs: "Bajo" },
                                { day: "Jue", val: 65, hrs: "Moderado" },
                                { day: "Vie", val: 45, hrs: "Bajo" },
                                { day: "Sáb", val: 20, hrs: "Óptimo" },
                                { day: "Dom", val: 25, hrs: "Óptimo" },
                              ]
                            : [
                                { day: "Lun", val: 60, hrs: "6k pasos" },
                                { day: "Mar", val: 75, hrs: "8k pasos" },
                                { day: "Mié", val: 90, hrs: "10k pasos" },
                                { day: "Jue", val: 55, hrs: "5k pasos" },
                                { day: "Vie", val: 80, hrs: "9k pasos" },
                                { day: "Sáb", val: 95, hrs: "12k pasos" },
                                { day: "Dom", val: 70, hrs: "7k pasos" },
                              ])
                          : (trackerTab === "stress" ? getStressChartData() : []);

                        return data.map((d, i) => (
                          <div key={i} className="w-full flex flex-col items-center group relative animate-fade-in" style={{ animationDelay: `${i * 100}ms` }}>
                            {/* Tooltip */}
                            <div className="absolute -top-10 scale-0 group-hover:scale-100 transition-transform origin-bottom bg-slate-900 text-white text-[9px] font-bold px-2 py-1 rounded shadow-md z-20 whitespace-nowrap">
                              {d.hrs}
                            </div>
                            <div className="w-full bg-slate-100 rounded-t-lg h-36 flex items-end">
                              <div 
                                className="w-full rounded-t-lg transition-all duration-700 bg-gradient-to-t from-[#00628F] to-[#2A9D8F] group-hover:opacity-90"
                                style={{ height: `${d.val}%` }}
                              ></div>
                            </div>
                            <span className="text-[10px] font-bold text-slate-400 mt-2">{d.day}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  ) : (
                    <div className="ml-10 flex-1 h-full flex flex-col items-center justify-center p-4 text-center bg-slate-50/50 backdrop-blur-sm border border-slate-100/50 rounded-2xl">
                      {(() => {
                        const iconProps = { size: 24, className: "text-[#00628F]/70 mb-2" };
                        const config = trackerTab === "sleep"
                          ? {
                              icon: <Moon {...iconProps} />,
                              title: "Sin datos de sueño",
                              subtitle: "Conecta tu wearable (Apple Health, Fitbit) o registra tus horas de descanso diarias en tu perfil para visualizar tu bitácora de recuperación de longevidad."
                            }
                          : trackerTab === "stress"
                          ? {
                              icon: <Brain {...iconProps} />,
                              title: "Sin datos de estrés",
                              subtitle: "Computamos tu estrés biológico a través de HRV de tus escaneos faciales biométricos de We360 FaceScan o bitácoras autónomas de bienestar mental."
                            }
                          : {
                              icon: <Footprints {...iconProps} />,
                              title: "Sin datos de actividad",
                              subtitle: "Sincroniza tus pasos y calorías diarias desde tu reloj o app de salud para mapear tu volumen de movimiento activo semanal."
                            };
                        return (
                          <>
                            {config.icon}
                            <h4 className="text-xs font-extrabold text-[#00628F] mb-1">{config.title}</h4>
                            <p className="text-[10px] text-slate-400 max-w-md leading-relaxed">{config.subtitle}</p>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* 6. SpO2 Card */}
              <div className="md:col-span-3 bg-white/60 backdrop-blur-md border border-white/20 p-5 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col justify-between min-h-[140px]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center text-[#00628F]">
                    <Activity size={16} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-600">Oxígeno (SpO2)</h4>
                </div>
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className={spo2Value !== null ? "text-2xl font-extrabold text-[#00628F]" : "text-lg font-extrabold text-[#00628F]/60"}>
                      {spo2Value !== null ? spo2Value : "Sin datos"}
                    </span>
                    {spo2Value !== null && (
                      <span className="text-[10px] text-slate-400 font-bold">%</span>
                    )}
                  </div>
                  <div className="w-full h-1 bg-slate-100 rounded-full mt-3 overflow-hidden">
                    <div className="h-full bg-[#2A9D8F] rounded-full" style={{ width: `${spo2Value !== null ? spo2Value : 0}%` }}></div>
                  </div>
                </div>
              </div>

              {/* 7. Temperatura Card */}
              <div className="md:col-span-3 bg-white/60 backdrop-blur-md border border-white/20 p-5 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col justify-between min-h-[140px]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-500">
                    <Moon size={16} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-600">Temperatura</h4>
                </div>
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className={tempValue !== null ? "text-2xl font-extrabold text-[#00628F]" : "text-lg font-extrabold text-[#00628F]/60"}>
                      {tempValue !== null ? tempValue : "Sin datos"}
                    </span>
                    {tempValue !== null && (
                      <span className="text-[10px] text-slate-400 font-bold">°C</span>
                    )}
                  </div>
                  {tempValue !== null ? (
                    <span className="text-[9px] font-bold text-[#2A9D8F] bg-[#2A9D8F]/10 px-2 py-0.5 rounded mt-2 inline-block">Normal</span>
                  ) : (
                    <span className="text-[9px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded mt-2 inline-block">Sin registros</span>
                  )}
                </div>
              </div>

              {/* 8. Frecuencia Respiratoria Card */}
              <div className="md:col-span-3 bg-white/60 backdrop-blur-md border border-white/20 p-5 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col justify-between min-h-[140px]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-500">
                    <TrendingUp size={16} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-600">Frec. Respiratoria</h4>
                </div>
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className={respRateValue !== null ? "text-2xl font-extrabold text-[#00628F]" : "text-lg font-extrabold text-[#00628F]/60"}>
                      {respRateValue !== null ? respRateValue : "Sin datos"}
                    </span>
                    {respRateValue !== null && (
                      <span className="text-[10px] text-slate-400 font-bold">rpm</span>
                    )}
                  </div>
                  <div className="flex gap-0.5 mt-3">
                    <div className={`h-1 flex-1 ${respRateValue !== null ? "bg-[#2A9D8F]" : "bg-slate-100"} rounded-full`}></div>
                    <div className={`h-1 flex-1 ${respRateValue !== null ? "bg-[#2A9D8F]" : "bg-slate-100"} rounded-full`}></div>
                    <div className="h-1 flex-1 bg-slate-100 rounded-full"></div>
                  </div>
                </div>
              </div>

              {/* 9. Glucosa Card */}
              <div className="md:col-span-3 bg-white/60 backdrop-blur-md border border-white/20 p-5 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col justify-between min-h-[140px]">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center text-[#2A9D8F]">
                    <Plus size={16} />
                  </div>
                  <h4 className="text-xs font-bold text-slate-600">Glucemia</h4>
                </div>
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className={glucoseValue !== null ? "text-2xl font-extrabold text-[#00628F]" : "text-lg font-extrabold text-[#00628F]/60"}>
                      {glucoseValue !== null ? glucoseValue : "Sin datos"}
                    </span>
                    {glucoseValue !== null && (
                      <span className="text-[10px] text-slate-400 font-bold">mg/dL</span>
                    )}
                  </div>
                  <span className="text-[9px] font-bold text-slate-400 mt-2 inline-block">
                    {glucoseValue !== null ? "Ayunas" : "Sin registros"}
                  </span>
                </div>
              </div>

            </div> {/* Closing Bento Grid */}

            {/* ── SECTION: BIOMARCADORES PREMIUM DE ESCANEO FACIAL (rPPG) ── */}
            <div className="mt-8 bg-white/40 backdrop-blur-md border border-white/20 p-6 rounded-3xl shadow-[0_12px_40px_rgba(0,0,0,0.03)] flex flex-col relative overflow-hidden">
              {/* Header with Title and Badges */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 border-b border-slate-100/50 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-md text-[9px] font-bold text-teal-700 bg-teal-50 border border-teal-200 uppercase tracking-wider">We360 FaceScan</span>
                    <span className="px-2 py-0.5 rounded-md text-[9px] font-bold text-[#00628F] bg-blue-50 border border-blue-200 uppercase tracking-wider">13 Biomarcadores</span>
                  </div>
                  <h3 className="text-base font-bold text-[#00628F] mt-1">Telemetría Avanzada por Escaneo Facial (rPPG)</h3>
                  <p className="text-[10px] text-slate-400 font-medium">Biomarcadores hemodinámicos y del sistema nervioso autónomo en tiempo real</p>
                </div>
                {(isDemo || (measurementsHistory && measurementsHistory.length > 0 && measurementsHistory.some(m => m.fields?.stressIndex != null))) && latestMeasurement?.createdAt && (
                  <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-xl">
                    Último escaneo: {(() => {
                      if (!latestMeasurement?.createdAt) return "—";
                      try {
                        let d = null;
                        if (typeof latestMeasurement.createdAt.toDate === "function") {
                          d = latestMeasurement.createdAt.toDate();
                        } else if (latestMeasurement.createdAt.seconds) {
                          d = new Date(latestMeasurement.createdAt.seconds * 1000);
                        } else {
                          d = new Date(latestMeasurement.createdAt);
                        }
                        if (d && !isNaN(d.getTime())) {
                          return d.toLocaleDateString("es-ES", { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
                        }
                      } catch (err) {
                        console.error(err);
                      }
                      return "—";
                    })()}
                  </span>
                )}
              </div>

              {/* Biomarkers Grid or Blurred State */}
              <div className="relative">
                {/* 13 Biomarkers Grid */}
                <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 ${(isDemo || (measurementsHistory && measurementsHistory.length > 0 && measurementsHistory.some(m => m.fields?.stressIndex != null))) ? "" : "blur-sm select-none pointer-events-none"}`}>
                  {BIOMARKERS_CONFIG.map((b) => {
                    const rawVal = getBiomarkerValue(b.key);
                    const val = rawVal !== null && rawVal !== undefined ? parseFloat(rawVal) : null;
                    const formattedVal = val !== null 
                      ? (b.key === "wellnessScore" 
                        ? (val <= 10 ? val.toFixed(1) : (val / 10).toFixed(1)) 
                        : (b.key === "cvdRisk" || b.key === "cardiovascularBMI" || b.key === "ansIndexPNS" || b.key === "ansIndexSNS" 
                          ? val.toFixed(1) 
                          : Math.round(val))) 
                      : null;

                    const gaugeConfig = getBiomarkerGaugeConfig(b.key, val);
                    const IconComponent = b.icon || Activity;

                    return (
                      <div key={b.key} className="bg-white/80 border border-white/40 p-4 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.01)] flex flex-col justify-between group relative hover:border-[#2A9D8F]/30 transition-all duration-300">
                        {/* Tooltip trigger on hover */}
                        <div className="absolute top-3 right-3 text-slate-300 hover:text-slate-500 cursor-help group-hover:text-[#00628F]/70 transition-colors">
                          <div className="relative">
                            <Info size={14} />
                            {/* Explanatory Tooltip */}
                            <div className="absolute bottom-full right-1/2 translate-x-1/2 mb-2 w-48 bg-slate-900 text-white text-[9px] font-medium leading-relaxed p-2 rounded-lg shadow-xl opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 transition-all origin-bottom pointer-events-none z-30">
                              {b.explanation}
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-7 h-7 rounded-lg bg-[#00628F]/5 flex items-center justify-center text-[#00628F]">
                              <IconComponent size={14} />
                            </div>
                            <span className="text-[11px] font-bold text-slate-500 tracking-tight">{b.label}</span>
                          </div>

                          <div className="flex items-baseline gap-1 mt-1">
                            <span className="text-xl font-extrabold text-[#00628F]">
                              {formattedVal !== null ? formattedVal : "—"}
                            </span>
                            {formattedVal !== null && b.unit && (
                              <span className="text-[10px] text-slate-400 font-bold">{b.unit}</span>
                            )}
                          </div>
                        </div>

                        {/* Visual PageSpeed-style horizontal gauge */}
                        <div className="mt-4">
                          {gaugeConfig ? (
                            <div>
                              {/* Horizontal bar containing colored segments */}
                              <div className="w-full h-1.5 bg-slate-100 rounded-full flex overflow-hidden relative">
                                {gaugeConfig.segments.map((seg, sIdx) => (
                                  <div 
                                    key={sIdx} 
                                    className="h-full first:rounded-l-full last:rounded-r-full"
                                    style={{ width: seg.width, backgroundColor: seg.color }}
                                    title={`${seg.label}: ${seg.range}`}
                                  ></div>
                                ))}
                                {/* Indicator marker absolute circle on top */}
                                <div 
                                  className="absolute top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-white border border-slate-700 shadow-sm transition-all duration-700"
                                  style={{ left: `${gaugeConfig.markerPosition}%` }}
                                ></div>
                              </div>
                              {/* Gauge Label Footer */}
                              <div className="flex justify-between items-center mt-1.5">
                                <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider">Estado</span>
                                <span className="text-[9px] font-extrabold uppercase tracking-wide" style={{ color: gaugeConfig.statusColor }}>
                                  {gaugeConfig.statusLabel}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1.5">
                              <div className="w-full h-1.5 bg-slate-100 rounded-full"></div>
                              <div className="flex justify-between items-center mt-1">
                                <span className="text-[8px] font-bold text-slate-300 uppercase tracking-wider">Estado</span>
                                <span className="text-[9px] font-extrabold text-slate-300 uppercase tracking-wide">Sin registros</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Blurred Overlay for real users with no scans */}
                {!(isDemo || (measurementsHistory && measurementsHistory.length > 0 && measurementsHistory.some(m => m.fields?.stressIndex != null))) && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/20 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/30 shadow-[inset_0_4px_30px_rgba(0,0,0,0.02)] z-10">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#00628F]/20 to-[#2A9D8F]/20 flex items-center justify-center text-[#00628F] mb-4 animate-pulse">
                      <Camera size={28} />
                    </div>
                    <h4 className="text-base font-extrabold text-[#00628F] mb-2">Desbloquea tus 13 Biomarcadores Premium</h4>
                    <p className="text-xs text-slate-500 max-w-lg leading-relaxed mb-5">
                      Realiza tu primer escaneo facial rPPG para evaluar tu variabilidad de frecuencia cardíaca (HRV), índice de estrés Baevsky, tono parasimpático y riesgo cardiovascular de manera instantánea y no invasiva con tu cámara.
                    </p>
                    <button
                      onClick={() => navigate("/medicion/nueva")}
                      className="px-6 py-3 rounded-xl bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white font-bold text-xs shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center gap-2"
                    >
                      <Camera size={14} />
                      Iniciar mi primer Escaneo Facial
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ═══ 4 BALANCES FUNDAMENTALES DE WELLNESS (Dr. Álvarez) ═══ */}
            <WellnessBalancesWidget
              profile={profile}
              isDemo={isDemo}
              latestMeasurement={latestMeasurement}
              onNavigateToDiet={() => navigate("/f4")}
              onNavigateToActivity={() => navigate("/f3")}
              className="mt-8 mb-8"
            />

            {/* ═══ WHATSAPP BIENESTAR EN TIEMPO REAL ═══ */}
            <WhatsAppDailyFeedWidget
              userId={targetUid}
              profile={profile}
              isDemo={isDemo}
              userPhone={profile?.whatsapp || profile?.telefono || profile?.phone}
              className="mb-8"
            />

            {/* ── SECTION: DETAILED DIMENSION CARDS ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              {/* Salud Clínica */}
              {hasF1 && (
                <CollapsibleCard
                  title="Salud Clínica"
                  subtitle={`IMC ${imc || "—"} (${imcCls?.label || "—"}) · PA ${bpCls?.label || "—"}`}
                  color="#00628F"
                  icon={Heart}
                  badge={imcCls?.label}
                  badgeColor={imcCls?.color}
                  testId="card-clinical"
                >
                  {(isOpen) => (
                    <div className="space-y-2 mt-2">
                      {imc && (
                        <div className="mb-3">
                          <ImcGauge imc={imc} classification={imcCls} animate={isOpen} />
                        </div>
                      )}
                      {imc && (
                        <CalculatedBadge label="IMC" value={imc} category={imcCls?.label} categoryColor={imcCls?.color} testId="db-badge-imc" />
                      )}
                      {bpCls && (
                        <CalculatedBadge label="Presión arterial" value={`${profile.paSistolica}/${profile.paDiastolica}`} unit="mmHg" category={bpCls?.label} categoryColor={bpCls?.color} testId="db-badge-bp" />
                      )}
                      {gluCls && (
                        <CalculatedBadge label="Glucemia" value={profile.glucemia} unit="mg/dl" category={gluCls?.label} categoryColor={gluCls?.color} testId="db-badge-glucose" />
                      )}
                      {profile.oximetria && (
                        <CalculatedBadge
                          label="Oximetría"
                          value={profile.oximetria}
                          unit="%"
                          category={parseFloat(profile.oximetria) < 95 ? "Baja" : "Normal"}
                          categoryColor={parseFloat(profile.oximetria) < 95 ? "#C0453A" : "#157346"}
                          testId="db-badge-oxi"
                        />
                      )}
                      {profile.cintura && (
                        <CalculatedBadge label="Cintura abdominal" value={profile.cintura} unit="cm" testId="db-badge-cintura" />
                      )}

                      <div className="rounded-xl p-3 mt-1 bg-slate-50/50 border border-slate-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide mb-2 text-slate-500">Estilo de vida</p>
                        <div className="flex flex-wrap gap-1.5">
                          {profile.tabaco && <InfoChip label={`Tabaco: ${profile.tabaco}`} />}
                          {profile.alcohol && <InfoChip label={`Alcohol: ${profile.alcohol}`} />}
                          {profile.sueno && <InfoChip label={`Sueño: ${profile.sueno}h`} color={parseFloat(profile.sueno) < 7 ? "#F9A01B" : "#157346"} />}
                        </div>
                      </div>

                      {(() => {
                        const allConds = [
                          ...(profile.condicionIds || []).map((id) => PREDEFINED_CONDITIONS.find((p) => p.id === id)?.nombre).filter(Boolean),
                          ...(profile.condicionesAdicionales || []).map((c) => c.nombre),
                        ];
                        return allConds.length > 0 && (
                          <div className="rounded-xl p-3 bg-slate-50/50 border border-slate-100">
                            <p className="text-[10px] font-bold uppercase tracking-wide mb-2 text-slate-500">Condiciones detectadas</p>
                            <div className="flex flex-wrap gap-1.5">
                              {allConds.map((c) => <InfoChip key={c} label={c} color="#C0453A" />)}
                            </div>
                          </div>
                        );
                      })()}

                      {profile.usaMedicamentos === "Si" && profile.medicamentos && (
                        <div className="rounded-xl p-3 bg-slate-50/50 border border-slate-100">
                          <p className="text-[10px] font-bold uppercase tracking-wide mb-1 text-slate-500">Medicamentos</p>
                          <div className="flex flex-wrap gap-1.5">
                            {Array.isArray(profile.medicamentos) ? (
                              profile.medicamentos.map((med, idx) => (
                                <span key={idx} className="text-xs px-2 py-1 rounded-full bg-[#E8F4F8] text-[#2D2A24]">
                                  {med.nombre}{med.dosis ? ` (${med.dosis})` : ""}
                                </span>
                              ))
                            ) : (
                              <p className="text-xs text-[#2D2A24]">{profile.medicamentos}</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CollapsibleCard>
              )}

              {/* Estado Físico */}
              {hasF2 && (
                <CollapsibleCard
                  title="Estado Físico"
                  subtitle={`PAR-Q ${profile.f2Results.parqAlerta ? `${profile.f2Results.parqRiskCount} ${profile.f2Results.parqRiskCount === 1 ? "riesgo" : "riesgos"}` : "Apto"} · 6MWT ${profile.f2Results.clasificacion6mwt}`}
                  color="#00628F"
                  icon={Activity}
                  badge={profile.f2Results.parqAlerta ? `${profile.f2Results.parqRiskCount} ${profile.f2Results.parqRiskCount === 1 ? "riesgo" : "riesgos"}` : "Apto"}
                  badgeColor={profile.f2Results.parqAlerta ? "#C0453A" : "#157346"}
                  testId="card-physical"
                >
                  <div className="space-y-2 mt-2">
                    <CalculatedBadge
                      label="PAR-Q"
                      value={profile.f2Results.parqAlerta ? `${profile.f2Results.parqRiskCount} ${profile.f2Results.parqRiskCount === 1 ? "riesgo detectado" : "riesgos detectados"}` : "Apto"}
                      categoryColor={profile.f2Results.parqAlerta ? "#C0453A" : "#157346"}
                      testId="db-badge-parq"
                    />
                    <CalculatedBadge label="Caminata 6 min" value={`${profile.f2Results.caminata6min}m`} category={profile.f2Results.clasificacion6mwt} categoryColor={semanticColor(profile.f2Results.clasificacion6mwt)} testId="db-badge-walk" />
                    <CalculatedBadge label="Sentadillas" value={`${profile.f2Results.sentadillas} reps`} category={profile.f2Results.clasificacionSentadillas} categoryColor={semanticColor(profile.f2Results.clasificacionSentadillas)} testId="db-badge-squats" />
                    <CalculatedBadge label="Flexiones" value={`${profile.f2Results.flexiones} reps`} category={profile.f2Results.clasificacionFlexiones} categoryColor={semanticColor(profile.f2Results.clasificacionFlexiones)} testId="db-badge-pushups" />
                    <CalculatedBadge label="Flexibilidad" value={profile.f2Results.clasificacionFlexibilidad} categoryColor={semanticColor(profile.f2Results.clasificacionFlexibilidad)} testId="db-badge-flex" />
                  </div>
                </CollapsibleCard>
              )}

              {/* Actividad Física */}
              {hasF3 && (
                <CollapsibleCard
                  title="Actividad Física"
                  subtitle={`${profile.f3Results.metTotal} MET-min/sem (${ipaqLabel(profile.f3Results.clasificacionIPAQ)})`}
                  color="#F9A01B"
                  icon={Footprints}
                  badge={ipaqLabel(profile.f3Results.clasificacionIPAQ)}
                  badgeColor={semanticColor(ipaqLabel(profile.f3Results.clasificacionIPAQ))}
                  testId="card-activity"
                >
                  <div className="space-y-2 mt-2">
                    <CalculatedBadge label="MET Total" value={profile.f3Results.metTotal} unit="MET-min/sem" category={ipaqLabel(profile.f3Results.clasificacionIPAQ)} categoryColor={semanticColor(ipaqLabel(profile.f3Results.clasificacionIPAQ))} testId="db-badge-met-total" />
                    <CalculatedBadge label="MET Vigorosa" value={profile.f3Results.vigorosa?.metMin ?? 0} unit="MET-min/sem" testId="db-badge-met-vig" />
                    <CalculatedBadge label="MET Moderada" value={profile.f3Results.moderada?.metMin ?? 0} unit="MET-min/sem" testId="db-badge-met-mod" />
                    <CalculatedBadge label="MET Caminata" value={profile.f3Results.caminata?.metMin ?? 0} unit="MET-min/sem" testId="db-badge-met-walk" />
                    <CalculatedBadge label="Sedentarismo" value={sedLabel(profile.f3Results.indiceSedentarismo)} categoryColor={semanticColor(sedLabel(profile.f3Results.indiceSedentarismo))} testId="db-badge-sed" />

                    {(profile.f3Results.indiceSedentarismo === "high" || profile.f3Results.indiceSedentarismo === "very_high") && (
                      <AlertBanner variant="warning" testId="db-alert-sed">
                        Pasar muchas horas sentado/a es un factor de riesgo independiente. Intenta hacer pausas activas cada hora.
                      </AlertBanner>
                    )}
                    {profile.f3Results.clasificacionIPAQ === "low" && (
                      <AlertBanner variant="info" testId="db-alert-oms">
                        La OMS recomienda al menos 150 min de actividad moderada o 75 min vigorosa por semana.
                      </AlertBanner>
                    )}
                  </div>
                </CollapsibleCard>
              )}

              {/* Nutrición */}
              {hasF4 && (
                <CollapsibleCard
                  title="Nutrición"
                  subtitle={`Dieta ${profile.f4Results.scoreCalidadDieta}/40 (${dietCatLabel(profile.f4Results.categoriaCalidadDieta)}) · Conductas ${profile.f4Results.scoreConductas}/15`}
                  color="#157346"
                  icon={Apple}
                  badge={dietCatLabel(profile.f4Results.categoriaCalidadDieta)}
                  badgeColor={semanticColor(dietCatLabel(profile.f4Results.categoriaCalidadDieta))}
                  testId="card-nutrition"
                >
                  {(isOpen) => (
                    <div className="space-y-2 mt-2">
                      <div className="mb-3">
                        <DietDonut score={profile.f4Results.scoreCalidadDieta} category={profile.f4Results.categoriaCalidadDieta} animate={isOpen} />
                      </div>
                      <CalculatedBadge label="Calidad de dieta" value={`${profile.f4Results.scoreCalidadDieta}/40`} category={dietCatLabel(profile.f4Results.categoriaCalidadDieta)} categoryColor={semanticColor(dietCatLabel(profile.f4Results.categoriaCalidadDieta))} testId="db-badge-diet" />
                      <CalculatedBadge label="Conductas alimentarias" value={`${profile.f4Results.scoreConductas}/15`} category={behaviorCatLabel(profile.f4Results.categoriaConductas)} categoryColor={semanticColor(behaviorCatLabel(profile.f4Results.categoriaConductas))} testId="db-badge-behavior" />
                      <CalculatedBadge label="Hidratación" value={hydrationLabel(profile.f4Results.hidratacion)} categoryColor={semanticColor(hydrationLabel(profile.f4Results.hidratacion))} testId="db-badge-hydration" />

                      <div className="rounded-xl p-3 mt-1 bg-slate-50/50 border border-slate-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide mb-1 text-slate-500">Motivación: {profile.f4Results.nivelMotivacion}</p>
                        {profile.f4Results.motivaciones?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {profile.f4Results.motivaciones.map((m) => <InfoChip key={m} label={m} color="#157346" />)}
                          </div>
                        )}
                      </div>

                      <div className="rounded-xl p-3 bg-slate-50/50 border border-slate-100">
                        <p className="text-[10px] font-bold uppercase tracking-wide mb-1 text-slate-500">Restricciones</p>
                        <p className="text-xs text-[#2D2A24]">
                          {profile.f4Results.restricciones?.length > 0
                            ? profile.f4Results.restricciones.join(", ")
                            : "Ninguna"}
                        </p>
                      </div>
                    </div>
                  )}
                </CollapsibleCard>
              )}

              {/* Bienestar Emocional */}
              {hasF5 && (
                <CollapsibleCard
                  title="Bienestar Emocional"
                  subtitle={`Score global ${profile.f5Results.scoreGlobal}/100 (${emotionalLabel(profile.f5Results.categoriaGlobal)})`}
                  color="#883476"
                  icon={Brain}
                  badge={`${profile.f5Results.scoreGlobal}/100`}
                  badgeColor={semanticColor(emotionalLabel(profile.f5Results.categoriaGlobal))}
                  testId="card-emotional"
                >
                  {(isOpen) => (
                    <div className="space-y-2 mt-2">
                      <div className="mb-3">
                        <EmotionalThermo score={profile.f5Results.scoreGlobal} animate={isOpen} />
                      </div>

                      <div
                        className="rounded-2xl p-4 text-center"
                        style={{
                          background: "linear-gradient(135deg, #883476 0%, #A04890 50%, #C070A0 100%)",
                          boxShadow: "0 6px 20px rgba(136,52,118,0.20)",
                        }}
                        data-testid="db-emotional-hero"
                      >
                        <div className="text-3xl font-extrabold text-white">{profile.f5Results.scoreGlobal}</div>
                        <p className="text-xs text-white mb-1" style={{ opacity: 0.7 }}>de 100</p>
                        <span className="inline-block text-[10px] font-bold px-2.5 py-0.5 rounded-lg bg-white/20 text-white">
                          {emotionalLabel(profile.f5Results.categoriaGlobal)}
                        </span>
                        <p className="text-[11px] mt-2 leading-relaxed text-white/90">
                          {getMotivationalPhrase(profile.f5Results.categoriaGlobal)}
                        </p>
                      </div>

                      <CalculatedBadge label="Ansiedad (GAD-5)" value={`${profile.f5Results.ansiedad?.score}/15`} category={anxietyLabel(profile.f5Results.ansiedad?.categoria)} categoryColor={semanticColor(anxietyLabel(profile.f5Results.ansiedad?.categoria))} testId="db-badge-anxiety" />
                      <CalculatedBadge label="Estrés / Burnout" value={`${profile.f5Results.estres?.score}/20`} category={stressLabel(profile.f5Results.estres?.categoria)} categoryColor={semanticColor(stressLabel(profile.f5Results.estres?.categoria))} testId="db-badge-stress" />
                      <CalculatedBadge label="Bienestar interno" value={`${profile.f5Results.bienestar?.score}/20`} category={wellbeingLabel(profile.f5Results.bienestar?.categoria)} categoryColor={semanticColor(wellbeingLabel(profile.f5Results.bienestar?.categoria))} testId="db-badge-wellbeing" />
                    </div>
                  )}
                </CollapsibleCard>
              )}

              {/* Historial de mediciones */}
              {hasF1 && (
                <div className="lg:col-span-2">
                  <CollapsibleCard
                    title="Historial de mediciones"
                    subtitle="Últimos registros de seguimiento"
                    color="#0F766E"
                    icon={ClipboardList}
                    testId="card-measurement-history"
                  >
                    {(isOpen) => <MeasurementHistoryContent isOpen={isOpen} />}
                  </CollapsibleCard>
                </div>
              )}
            </div>

            {/* ═══ FOOTER ═══ */}
            <div className="text-center py-8 border-t border-slate-100 mt-10" data-testid="dashboard-footer">
              <p className="text-xs font-bold text-slate-400">
                WE 360 · Tu salud, tu camino
              </p>
              <p className="text-[10px] mt-1.5 leading-relaxed text-slate-400 max-w-md mx-auto">
                Este informe es orientativo y no reemplaza una consulta médica profesional.
              </p>
              <p className="text-[10px] mt-1 text-slate-400">
                Generado el {new Date().toLocaleDateString("es-CO")}
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* Botón Flotante (FAB) para Nueva Medición en Mobile */}
      {hasF1 && (
        <button
          onClick={() => navigate("/medicion/nueva")}
          className="md:hidden fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-[#00628F] to-[#2A9D8F] text-white flex items-center justify-center shadow-[0_8px_30px_rgba(0,98,143,0.3)] hover:scale-105 active:scale-95 transition-all cursor-pointer border border-white/20 animate-fab-float"
          data-testid="mobile-fab-new-measurement"
          aria-label="Nueva Medición"
        >
          <Camera size={24} />
        </button>
      )}
    </div>
  );
}

// ── Small helper components ──────────────────────────────
function InfoChip({ label, color = "#71644C" }) {
  return (
    <span
      className="text-[10px] font-semibold px-2 py-0.5 rounded-md"
      style={{ background: `${color}12`, color }}
    >
      {label}
    </span>
  );
}

function getMotivationalPhrase(key) {
  const phrases = {
    critical: "Es importante que busques apoyo. No tienes que enfrentar esto solo/a.",
    low: "Hay áreas que puedes mejorar. Pequeños pasos hacen gran diferencia.",
    moderate: "Vas por buen camino. Hay espacio para fortalecer tu bienestar.",
    good: "¡Bien hecho! Tienes buenos recursos emocionales.",
    excellent: "¡Excelente! Tu bienestar emocional es muy sólido.",
  };
  return phrases[key] || "";
}
